import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { motion, AnimatePresence } from 'framer-motion';
import { AppShell3 } from '@/components/app-shell-3';
import { ToolHeader } from '@/components/ToolHeader';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle
} from '@/components/ui/card';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from '@/components/ui/accordion';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Progress as ProgressBar } from '@/components/ui/progress';
import { Progress } from '@/components/application/progress-steps/progress-steps';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '@/components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/lib/supabase';
import { gcsUploadFile } from '@/lib/gcsStorage';
import { useSpaceData } from '@/hooks/useSpaceData';
import { useAuth } from '@/contexts/AuthContext';
import {
  Bot,
  Send,
  Plus,
  Edit,
  Trash2,
  Wand2,
  MessageSquare,
  ChevronRight,
  Building,
  Users,
  FileText,
  AlertCircle,
  CheckCircle,
  Clock,
  Shield,
  Loader2,
  AlertTriangle,
  Save,
  RefreshCw,
  MapPin,
  Briefcase,
  FileCheck,
  XCircle,
  CheckCircle2,
  Info,
  ChevronDown,
  LayoutGrid,
  BookOpen,
  Download,
  Settings,
  ChevronLeft,
  List,
  PanelLeftOpen,
  PanelRightOpen,
  X,
  Check,
  ListChecks,
  Link,
  Upload,
  HardHat,
  Zap,
  Droplets,
  Wrench,
  Flame,
  Gauge,
  Leaf,
  Lock,
  ArrowUpDown,
  Accessibility,
  Filter,
  ExternalLink,
  Award,
  CheckSquare
} from 'lucide-react';
import { TOOL_REGISTRY as toolConfigs } from '@/config/tools';
import { getOpenAI } from '@/lib/openai';
import { siteComplianceService } from '@/lib/siteComplianceService';
import { cn } from '@/lib/utils';
import MessageFormatter from '@/components/MessageFormatter';
import { toast } from 'sonner';
import { PageTransition } from '@/components/PageTransition';
import { ChatFlowsEmbed } from 'chatflows-embed';
import {
  registerSiteComplianceSkills,
  sendSiteInitialContext
} from './site-chatflows-skills';
import { LinkDocumentDialog } from '@/components/compliance/LinkDocumentDialog';

const SiteCompliancePage = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { user, userProfile, company } = useAuth();

  // Use the cached space data hook
  // Pass company.id to validate the space belongs to the user's current company
  const { space, spaceTools, loading: spaceLoading, accessDenied } = useSpaceData(projectId, userProfile?.id, company?.id);

  // Set page title
  usePageTitle(space?.name ? `${space.name} Site Compliance` : 'Site Compliance');

  // Redirect if user doesn't have access (e.g., switched companies)
  useEffect(() => {
    if (accessDenied) {
      toast.error('You no longer have access to this space');
      navigate('/dashboard', { replace: true });
    }
  }, [accessDenied, navigate]);

  const [wizardStep, setWizardStep] = useState(1);
  // Site requirements (site-level compliance items like inspections, certificates, permits)
  const [siteRequirements, setSiteRequirements] = useState([]);
  const [messages, setMessages] = useState([]);
  const [userMessage, setUserMessage] = useState('');
  const [wizardMessage, setWizardMessage] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isChatting, setIsChatting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedRequirement, setSelectedRequirement] = useState(null);
  const [lastSaved, setLastSaved] = useState(null);
  const [editingRequirement, setEditingRequirement] = useState(null);
  const [editFormData, setEditFormData] = useState({});
  const [teamMembers, setTeamMembers] = useState([]);
  const [showClearConfirmDialog, setShowClearConfirmDialog] = useState(false);

  // Link document dialog state
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkingRequirement, setLinkingRequirement] = useState(null);

  // Link task/workflow dialog state (for checklists)
  const [linkTaskWorkflowDialogOpen, setLinkTaskWorkflowDialogOpen] = useState(false);
  const [linkingChecklistRequirement, setLinkingChecklistRequirement] = useState(null);
  const [spaceTasks, setSpaceTasks] = useState([]);
  const [spaceWorkflows, setSpaceWorkflows] = useState([]);
  const [loadingTasksWorkflows, setLoadingTasksWorkflows] = useState(false);

  // File upload state
  const fileInputRef = useRef(null);
  const [uploadingRequirement, setUploadingRequirement] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Filter state for requirements list
  const [activeFilters, setActiveFilters] = useState(['legal', 'document', 'checklist']);

  // Toggle a filter on/off
  const toggleFilter = (filter) => {
    setActiveFilters(prev =>
      prev.includes(filter)
        ? prev.filter(f => f !== filter)
        : [...prev, filter]
    );
  };

  // Filter requirements based on active filters
  const filteredSiteRequirements = useMemo(() => {
    if (activeFilters.length === 3) return siteRequirements; // All filters active, show all
    if (activeFilters.length === 0) return []; // No filters, show nothing

    return siteRequirements.filter(req => {
      // Check each active filter
      const matchesLegal = activeFilters.includes('legal') && req.legal_requirement;
      const matchesDocument = activeFilters.includes('document') && req.requirement_type === 'documentation';
      const matchesChecklist = activeFilters.includes('checklist') && req.requirement_type === 'internal_checklist';

      return matchesLegal || matchesDocument || matchesChecklist;
    });
  }, [siteRequirements, activeFilters]);

  // Wizard mode state
  const [wizardMode, setWizardMode] = useState(true);

  // Site compliance wizard state - site-specific fields
  // Site info
  const [siteType, setSiteType] = useState(null);  // office, warehouse, factory, construction_site, retail, mixed
  const [buildingAge, setBuildingAge] = useState(null);  // pre_1900, 1900_1960, 1960_2000, post_2000
  const [buildingOwnership, setBuildingOwnership] = useState(null);  // owned, leased, shared
  const [floorCount, setFloorCount] = useState(null);
  const [totalAreaSqft, setTotalAreaSqft] = useState(null);

  // Systems
  const [fireSystems, setFireSystems] = useState(null);
  const [electricalSystems, setElectricalSystems] = useState(null);
  const [hvacSystems, setHvacSystems] = useState(null);
  const [waterSystems, setWaterSystems] = useState(null);
  const [gasSystems, setGasSystems] = useState(null);  // boolean

  // Hazards
  const [asbestosStatus, setAsbestosStatus] = useState(null);  // none, surveyed_safe, managed, unknown
  const [legionellaRisk, setLegionellaRisk] = useState(null);
  const [hazardousStorage, setHazardousStorage] = useState(null);

  // Equipment
  const [fixedEquipment, setFixedEquipment] = useState(null);
  const [liftingEquipment, setLiftingEquipment] = useState(null);  // LOLER relevant
  const [pressureSystems, setPressureSystems] = useState(null);  // PSSR relevant

  // Access
  const [publicAccess, setPublicAccess] = useState(null);  // boolean
  const [disabledAccess, setDisabledAccess] = useState(null);  // boolean

  // Generation options toggles (site compliance focused)
  const [generateInspections, setGenerateInspections] = useState(true);
  const [generateCertificates, setGenerateCertificates] = useState(true);
  const [generateDocumentation, setGenerateDocumentation] = useState(true);
  const [settingsExpanded, setSettingsExpanded] = useState(false);


  // Legacy wizardState object for backward compatibility (not actively used with ChatFlows)
  const wizardState = {
    collectedData: {
      site_type: siteType,
      building_age: buildingAge,
      building_ownership: buildingOwnership,
      floor_count: floorCount,
      total_area_sqft: totalAreaSqft,
      fire_systems: fireSystems,
      electrical_systems: electricalSystems,
      water_systems: waterSystems,
      gas_systems: gasSystems,
      asbestos_status: asbestosStatus,
      legionella_risk: legionellaRisk,
      lifting_equipment: liftingEquipment,
      pressure_systems: pressureSystems
    },
    sectionSummaries: {},
    expandedContext: {},
    currentSection: null
  };
  // Removed inline editing states - now using text areas
  const [suggestedResponses, setSuggestedResponses] = useState([]);
  const [showRegenerateButton, setShowRegenerateButton] = useState(false);

  // Panel visibility states
  const [showWizardPanel, setShowWizardPanel] = useState(false);
  const [showRequirementsPanel, setShowRequirementsPanel] = useState(false);

  // Refs for auto-scrolling
  const chatEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const chatInputRef = useRef(null);
  const chatFlowsRef = useRef(null);

  // Safety net: Track which requirements have been processed this session
  // Prevents infinite loops even if Supabase status updates fail
  const processedRequirementsRef = useRef(new Set());

  // Generation progress state for dynamic loading
  const [generationProgress, setGenerationProgress] = useState({
    stage: 0,
    message: 'Initializing AI analysis...',
    substeps: []
  });

  // Load existing compliance data on mount
  useEffect(() => {
    const initializeData = async () => {
      await fetchTeamMembers(); // Load team members
      await loadComplianceData(space); // Pass space data to loadComplianceData
    };
    if (space) {
      initializeData();
    }
  }, [projectId, space]);

  // Realtime subscription to listen for site requirement updates
  useEffect(() => {
    if (!projectId) return;

    console.log('📡 Setting up realtime subscription for site_compliance_requirements');

    const channel = supabase
      .channel(`site-compliance-${projectId}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'site_compliance_requirements',
          filter: `project_id=eq.${projectId}`
        },
        (payload) => {
          console.log('📡 Realtime update received:', payload.new?.name, payload.new?.status);

          // Update the specific requirement in state
          setSiteRequirements(prev => prev.map(r =>
            r.id === payload.new.id ? { ...r, ...payload.new } : r
          ));
        }
      )
      .subscribe((status) => {
        console.log('📡 Realtime subscription status:', status);
      });

    return () => {
      console.log('📡 Cleaning up realtime subscription');
      supabase.removeChannel(channel);
    };
  }, [projectId]);

  // Cleanup on unmount - clear session when leaving page
  useEffect(() => {
    return () => {
      if (chatFlowsRef.current?.clearSession) {
        console.log('🧹 Cleaning up ChatFlows session on page exit');
        chatFlowsRef.current.clearSession();
      }
    };
  }, []);

  // Fetch team members for context
  const fetchTeamMembers = async () => {
    try {
      const { data, error } = await supabase
        .from('space_members')
        .select(
          `
          *,
          user_profiles!user_id (
            id,
            first_name,
            last_name,
            email,
            avatar_url,
            job_title
          )
        `
        )
        .eq('project_id', projectId)
        .order('created_date', { ascending: false });

      if (error) {
        console.error('Error fetching team members:', error);
        setTeamMembers([]);
      } else {
        // Transform to match expected structure
        const formattedMembers = (data || []).map(sm => ({
          id: sm.id, // space_members.id (used in compliance_assignments.team_member_id)
          space_member_id: sm.id, // Explicit space_members.id
          user_id: sm.user_id, // user_profiles.id
          name:
            `${sm.user_profiles.first_name || ''} ${
              sm.user_profiles.last_name || ''
            }`.trim() || sm.user_profiles.email,
          email: sm.user_profiles.email,
          role: sm.user_profiles.job_title,
          department: sm.department,
          status: sm.status,
          avatar_url: sm.user_profiles.avatar_url,
          compliance_percentage: sm.compliance_percentage
        }));

        setTeamMembers(formattedMembers);
      }
    } catch (error) {
      console.error('Error fetching team members:', error);
      setTeamMembers([]);
    }
  };

  // Debug: Log site requirements changes
  useEffect(() => {
    console.log('🔍 Site requirements state changed:', siteRequirements.length, 'items');
    console.log('🔍 isGenerating:', isGenerating);
  }, [siteRequirements, isGenerating]);

  // Debug: Log generation toggle state changes
  useEffect(() => {
    console.log('🎚️ Toggle states updated:', {
      generateInspections,
      generateCertificates,
      generateDocumentation
    });
  }, [generateInspections, generateCertificates, generateDocumentation]);

  // Auto-save when site requirements change
  useEffect(() => {
    if (siteRequirements.length > 0 && !isLoading) {
      const timer = setTimeout(() => {
        saveComplianceData();
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [siteRequirements]);

  // Helper function to get all embed data - always sends complete site compliance data
  const getAllSiteEmbedData = useCallback(() => {
    const embedData = {
      // Site info
      site_type: siteType,
      building_age: buildingAge,
      building_ownership: buildingOwnership,
      floor_count: floorCount,
      total_area_sqft: totalAreaSqft,
      // Systems
      fire_systems: fireSystems,
      electrical_systems: electricalSystems,
      hvac_systems: hvacSystems,
      water_systems: waterSystems,
      gas_systems: gasSystems,
      // Hazards
      asbestos_status: asbestosStatus,
      legionella_risk: legionellaRisk,
      hazardous_storage: hazardousStorage,
      // Equipment
      fixed_equipment: fixedEquipment,
      lifting_equipment: liftingEquipment,
      pressure_systems: pressureSystems,
      // Access
      public_access: publicAccess,
      disabled_access: disabledAccess,
      // Space info
      space_name: space?.name,
      space_id: projectId
    };

    // Include generated_requirements if siteRequirements exist
    if (siteRequirements && siteRequirements.length > 0) {
      embedData.generated_requirements = siteRequirements;
    }

    return embedData;
  }, [
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    space,
    projectId,
    siteRequirements
  ]);

  // Auto-save site wizard data when it changes
  useEffect(() => {
    if (
      !isLoading &&
      (siteType ||
        buildingAge ||
        buildingOwnership ||
        floorCount ||
        totalAreaSqft ||
        fireSystems ||
        electricalSystems ||
        hvacSystems ||
        waterSystems ||
        gasSystems !== null ||
        asbestosStatus ||
        legionellaRisk ||
        hazardousStorage ||
        fixedEquipment ||
        liftingEquipment ||
        pressureSystems ||
        publicAccess !== null ||
        disabledAccess !== null)
    ) {
      const timer = setTimeout(() => {
        siteComplianceService.saveSiteWizardData(projectId, {
          site_type: siteType,
          building_age: buildingAge,
          building_ownership: buildingOwnership,
          floor_count: floorCount,
          total_area_sqft: totalAreaSqft,
          fire_systems: fireSystems,
          electrical_systems: electricalSystems,
          hvac_systems: hvacSystems,
          water_systems: waterSystems,
          gas_systems: gasSystems,
          asbestos_status: asbestosStatus,
          legionella_risk: legionellaRisk,
          hazardous_storage: hazardousStorage,
          fixed_equipment: fixedEquipment,
          lifting_equipment: liftingEquipment,
          pressure_systems: pressureSystems,
          public_access: publicAccess,
          disabled_access: disabledAccess
        });
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    projectId,
    isLoading
  ]);

  // Set up window.ChatflowsData for initial session context
  useEffect(() => {
    window.ChatflowsData = () => {
      const configData = getAllSiteEmbedData();
      console.log('[SITE COMPLIANCE] ChatflowsData returning context:', configData);
      return configData;
    };

    return () => {
      delete window.ChatflowsData;
    };
  }, [getAllSiteEmbedData]);

  // Update ChatFlows embed when configuration changes
  useEffect(() => {
    if (
      !isLoading &&
      chatFlowsRef.current &&
      (siteType ||
        buildingAge ||
        buildingOwnership ||
        floorCount ||
        fireSystems ||
        electricalSystems ||
        waterSystems ||
        asbestosStatus ||
        legionellaRisk ||
        liftingEquipment ||
        pressureSystems)
    ) {
      chatFlowsRef.current.update(getAllSiteEmbedData());
    }
  }, [
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    isLoading,
    getAllSiteEmbedData
  ]);

  // Auto-scroll chat to bottom when new messages arrive
  useEffect(() => {
    if (chatEndRef.current) {
      chatEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Helper function to add a message (no longer saves to database - embed handles persistence)
  const addMessage = message => {
    // Validate message content
    if (!message.content || !message.content.trim()) {
      console.warn('Attempted to add empty message:', message);
      return;
    }

    setMessages(prev => [...prev, message]);
  };

  // Helper function to add multiple messages
  const addMessages = newMessages => {
    // Filter out empty messages
    const validMessages = newMessages.filter(
      msg => msg.content && msg.content.trim()
    );

    if (validMessages.length === 0) {
      console.warn('Attempted to add empty messages:', newMessages);
      return;
    }

    setMessages(prev => [...prev, ...validMessages]);
  };

  // Clear chat messages
  const clearChatMessages = async () => {
    setMessages([
      {
        role: 'assistant',
        content:
          '👋 Chat history cleared. How can I help you with site compliance today?'
      }
    ]);
    // Site compliance doesn't use chat messages table - handled by ChatFlows embed
  };

  // Reset all site compliance data (for debugging)
  const resetComplianceData = async () => {
    setIsLoading(true);

    try {
      // Clear all site compliance requirements
      await siteComplianceService.deleteAllSiteRequirements(projectId);

      // Clear site-specific wizard data from unified compliance_wizard_data table
      // Only clear site fields, preserve team compliance fields
      const { error } = await supabase
        .from('compliance_wizard_data')
        .update({
          site_type: null,
          building_age: null,
          building_ownership: null,
          floor_count: null,
          total_area_sqft: null,
          fire_systems: null,
          electrical_systems: null,
          hvac_systems: null,
          water_systems: null,
          gas_systems: null,
          asbestos_status: null,
          legionella_risk: null,
          hazardous_storage: null,
          fixed_equipment: null,
          lifting_equipment: null,
          pressure_systems: null,
          public_access: null,
          disabled_access: null,
          updated_date: new Date().toISOString()
        })
        .eq('project_id', projectId);

      if (error) {
        console.error('[SITE COMPLIANCE] Error clearing wizard data:', error);
      }

      // Reset all local state to initial values
      setSiteRequirements([]);
      setMessages([]);
      setUserMessage('');
      setWizardMode(true);
      // Reset site-specific state
      setSiteType(null);
      setBuildingAge(null);
      setBuildingOwnership(null);
      setFloorCount(null);
      setTotalAreaSqft(null);
      setFireSystems(null);
      setElectricalSystems(null);
      setHvacSystems(null);
      setWaterSystems(null);
      setGasSystems(null);
      setAsbestosStatus(null);
      setLegionellaRisk(null);
      setHazardousStorage(null);
      setFixedEquipment(null);
      setLiftingEquipment(null);
      setPressureSystems(null);
      setPublicAccess(null);
      setDisabledAccess(null);
      setEditingRequirement(null);

      // Reload fresh data (which will now be empty and start wizard)
      await loadSiteComplianceData(space);
    } catch (error) {
      console.error('[SITE COMPLIANCE] Error resetting compliance data:', error);
      alert('Failed to reset site compliance data. Check console for errors.');
    } finally {
      setIsLoading(false);
    }
  };

  const loadSiteComplianceData = async (spaceData = null) => {
    setIsLoading(true);
    const currentSpace = spaceData || space; // Use passed space data or state

    // Load site compliance data (chat is handled by embed now)
    const result = await siteComplianceService.loadProjectSiteCompliance(projectId);

    if (result.success) {
      if (result.requirements.length > 0) {
        setSiteRequirements(result.requirements);
        // Auto-show requirements panel if there are requirements
        setShowRequirementsPanel(true);
      }

      // Load site wizard data from database - set site-specific state variables
      if (result.wizardData) {
        // Site info
        setSiteType(result.wizardData.site_type ?? null);
        setBuildingAge(result.wizardData.building_age ?? null);
        setBuildingOwnership(result.wizardData.building_ownership ?? null);
        setFloorCount(result.wizardData.floor_count ?? null);
        setTotalAreaSqft(result.wizardData.total_area_sqft ?? null);
        // Systems
        setFireSystems(result.wizardData.fire_systems ?? null);
        setElectricalSystems(result.wizardData.electrical_systems ?? null);
        setHvacSystems(result.wizardData.hvac_systems ?? null);
        setWaterSystems(result.wizardData.water_systems ?? null);
        setGasSystems(result.wizardData.gas_systems ?? null);
        // Hazards
        setAsbestosStatus(result.wizardData.asbestos_status ?? null);
        setLegionellaRisk(result.wizardData.legionella_risk ?? null);
        setHazardousStorage(result.wizardData.hazardous_storage ?? null);
        // Equipment
        setFixedEquipment(result.wizardData.fixed_equipment ?? null);
        setLiftingEquipment(result.wizardData.lifting_equipment ?? null);
        setPressureSystems(result.wizardData.pressure_systems ?? null);
        // Access
        setPublicAccess(result.wizardData.public_access ?? null);
        setDisabledAccess(result.wizardData.disabled_access ?? null);

        // Auto-show wizard panel if there's wizard data
        if (
          result.wizardData.site_type ||
          result.wizardData.building_age ||
          result.wizardData.fire_systems ||
          result.wizardData.electrical_systems ||
          result.wizardData.water_systems
        ) {
          setShowWizardPanel(true);
        }

        // If we have wizard data, don't show wizard mode initially
        if (result.wizardData.site_type || result.wizardData.fire_systems) {
          setWizardMode(false);
        }
      }

      if (result.requirements.length === 0 && !result.wizardData) {
        // No wizard data and no requirements - start wizard
        setWizardMode(true);
      }

      // Send initial configuration to ChatFlows embed on load
      if (chatFlowsRef.current && result.wizardData) {
        // Always send all data
        chatFlowsRef.current.update(getAllSiteEmbedData());
      }
    }

    setIsLoading(false);
  };

  // Alias for backward compatibility
  const loadComplianceData = loadSiteComplianceData;

  const saveComplianceData = async () => {
    setIsSaving(true);

    // Site wizard data is auto-saved via useEffect, so just update ChatFlows embed
    if (chatFlowsRef.current) {
      chatFlowsRef.current.update(getAllSiteEmbedData());
    }

    setLastSaved(new Date());
    setIsSaving(false);
  };

  const handleWizardSubmit = useCallback(async (projectDataOverride = null) => {
    console.log('[SITE COMPLIANCE] Starting handleWizardSubmit...');
    setIsGenerating(true);
    setWizardMessage('Analyzing site compliance requirements with AI...');

    try {
      // Prepare site-specific context for N8N
      const contextPayload = {
        projectId: projectId,
        companyId: space?.company_id || company?.id,
        companyName: company?.name,

        // Space/Project Details
        space: {
          id: projectId,
          name: space?.name,
          type: space?.type,
          industry: space?.industry,
          location: space?.location,
          description: space?.description
        },

        // Collected site wizard data
        siteData: {
          // Site info
          site_type: siteType,
          building_age: buildingAge,
          building_ownership: buildingOwnership,
          floor_count: floorCount,
          total_area_sqft: totalAreaSqft,
          // Systems
          fire_systems: fireSystems,
          electrical_systems: electricalSystems,
          hvac_systems: hvacSystems,
          water_systems: waterSystems,
          gas_systems: gasSystems,
          // Hazards
          asbestos_status: asbestosStatus,
          legionella_risk: legionellaRisk,
          hazardous_storage: hazardousStorage,
          // Equipment
          fixed_equipment: fixedEquipment,
          lifting_equipment: liftingEquipment,
          pressure_systems: pressureSystems,
          // Access
          public_access: publicAccess,
          disabled_access: disabledAccess
        },

        // Generation options (site compliance focused)
        generationOptions: {
          generateInspections: generateInspections,
          generateCertificates: generateCertificates,
          generateDocumentation: generateDocumentation
        },

        // User context
        user: {
          id: user?.id,
          email: user?.email,
          name: userProfile?.full_name || user?.email,
          role: userProfile?.role
        }
      };

      console.log('[SITE COMPLIANCE] Generation toggle states:', {
        generateInspections,
        generateCertificates,
        generateDocumentation
      });
      console.log('[SITE COMPLIANCE] Sending context to N8N:', contextPayload);

      // Call N8N webhook for SITE compliance (different from team compliance)
      const response = await fetch(
        'https://n8n.add.ai/webhook/generate-site-compliance-results',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(contextPayload)
        }
      );

      console.log('[SITE COMPLIANCE] N8N webhook response status:', response.status);

      if (!response.ok) {
        throw new Error(
          `N8N webhook failed: ${response.status} ${response.statusText}`
        );
      }

      const result = await response.json();
      console.log('[SITE COMPLIANCE] N8N webhook completed');

      // N8N has already saved everything to the database
      // Just fetch all site compliance data from database
      console.log('[SITE COMPLIANCE] Fetching all site compliance requirements from database...');
      const loadResult = await siteComplianceService.loadProjectSiteCompliance(projectId);

      if (loadResult.success && loadResult.requirements) {
        console.log('[SITE COMPLIANCE] Loaded', loadResult.requirements.length, 'requirements from database');
        setSiteRequirements(loadResult.requirements);

        // Show requirements panel
        setShowRequirementsPanel(true);

        const count = loadResult.requirements.length;
        const successMessage = `Successfully generated ${count} site compliance requirements.`;
        setWizardMessage(successMessage);

        // Show success toast
        toast.success(`Generated ${count} site compliance requirements`, {
          description: 'Requirements have been saved and are now visible in the preview panel'
        });

        console.log('[SITE COMPLIANCE] All data loaded and displayed successfully');
      } else {
        console.error('[SITE COMPLIANCE] Failed to load requirements from database');
        toast.error('Failed to load requirements', {
          description: 'Unable to fetch requirements from database. Please refresh the page.'
        });
      }
    } catch (error) {
      const errorMessage = 'An error occurred while generating site compliance requirements.';
      setWizardMessage(errorMessage);
      console.error('[SITE COMPLIANCE] Error calling N8N:', error);

      // Show error toast with details
      toast.error('Generation failed', {
        description: error.message || 'Unable to connect to the AI service. Please check your connection and try again.'
      });
    } finally {
      console.log('[SITE COMPLIANCE] Finished handleWizardSubmit, stopping loader...');
      setIsGenerating(false);
    }
  }, [
    projectId,
    space,
    company,
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    generateInspections,
    generateCertificates,
    generateDocumentation,
    user,
    userProfile
  ]);

  const generateSiteRequirementsFromWizard = useCallback(async () => {
    // Use direct site-specific state variables for requirement generation
    const siteData = {
      // Site info
      siteType: siteType || '',
      buildingAge: buildingAge || '',
      buildingOwnership: buildingOwnership || '',
      floorCount: floorCount || null,
      totalAreaSqft: totalAreaSqft || null,
      // Systems
      fireSystems: fireSystems || '',
      electricalSystems: electricalSystems || '',
      hvacSystems: hvacSystems || '',
      waterSystems: waterSystems || '',
      gasSystems: gasSystems || false,
      // Hazards
      asbestosStatus: asbestosStatus || '',
      legionellaRisk: legionellaRisk || '',
      hazardousStorage: hazardousStorage || '',
      // Equipment
      fixedEquipment: fixedEquipment || '',
      liftingEquipment: liftingEquipment || '',
      pressureSystems: pressureSystems || '',
      // Access
      publicAccess: publicAccess || false,
      disabledAccess: disabledAccess || false
    };

    // Call the wizard submit with the collected site data
    await handleWizardSubmit(siteData);

    // Update ChatFlows embed with new configuration
    if (chatFlowsRef.current) {
      chatFlowsRef.current.update(getAllSiteEmbedData());
    }

    setWizardMode(false); // Switch chat to management mode
    await addMessage({
      role: 'assistant',
      content:
        "I've generated your site compliance requirements based on the information you provided. You can now manage these requirements - add, edit, or remove items as needed. Just tell me what you'd like to do!"
    });
  }, [
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    handleWizardSubmit,
    getAllSiteEmbedData,
    addMessage
  ]);

  // Alias for backward compatibility
  const generateRequirementsFromWizard = generateSiteRequirementsFromWizard;

  // Re-register skills when toggle states change
  useEffect(() => {
    if (!chatFlowsRef.current || !projectId) {
      return;
    }

    console.log('[SITE COMPLIANCE] Re-registering skills due to toggle change...');

    const siteSkillContext = {
      // Site-specific state setters
      setSiteType,
      setBuildingAge,
      setBuildingOwnership,
      setFloorCount,
      setTotalAreaSqft,
      setFireSystems,
      setElectricalSystems,
      setHvacSystems,
      setWaterSystems,
      setGasSystems,
      setAsbestosStatus,
      setLegionellaRisk,
      setHazardousStorage,
      setFixedEquipment,
      setLiftingEquipment,
      setPressureSystems,
      setPublicAccess,
      setDisabledAccess,
      // Current site values
      siteType,
      buildingAge,
      buildingOwnership,
      floorCount,
      totalAreaSqft,
      fireSystems,
      electricalSystems,
      hvacSystems,
      waterSystems,
      gasSystems,
      asbestosStatus,
      legionellaRisk,
      hazardousStorage,
      fixedEquipment,
      liftingEquipment,
      pressureSystems,
      publicAccess,
      disabledAccess,
      // Common state
      setIsGenerating: setIsSaving,
      projectId,
      siteRequirements: siteRequirements,
      setSiteRequirements: setSiteRequirements,
      siteComplianceService,
      generateSiteRequirementsFromWizard,
      getAllSiteEmbedData
    };

    try {
      registerSiteComplianceSkills(chatFlowsRef, siteSkillContext);
      console.log('[SITE COMPLIANCE] Skills re-registered with new toggle values:', {
        generateInspections,
        generateCertificates,
        generateDocumentation
      });
    } catch (error) {
      console.error('[SITE COMPLIANCE] Error re-registering skills:', error);
    }
  }, [
    generateInspections,
    generateCertificates,
    generateDocumentation,
    generateSiteRequirementsFromWizard,
    projectId,
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    siteRequirements,
    getAllSiteEmbedData
  ]);

  // Handler for when ChatFlows embed is ready
  const handleChatFlowsReady = useCallback(() => {
    console.log(
      '[SITE COMPLIANCE] ChatFlows embed ready - checking conditions...'
    );

    if (chatFlowsRef.current && space) {
      console.log('[SITE COMPLIANCE] All conditions met - initializing...');

      // Longer delay to ensure iframe JavaScript is ready to receive messages
      setTimeout(() => {
        // 0. Clear any existing session first
        console.log(
          '[SITE COMPLIANCE] Clearing existing session for space:',
          projectId
        );
        if (chatFlowsRef.current?.clearSession) {
          chatFlowsRef.current.clearSession();
        }

        // Wait a bit for session to clear before initializing new one
        setTimeout(() => {
          // Get all site embed data
          const embedData = getAllSiteEmbedData();

          console.log(
            '[SITE COMPLIANCE] Initializing ChatFlows session with embed data'
          );
          console.log('[SITE COMPLIANCE] Embed data:', embedData);

          // Send session_update to reinitialize session with space as user
          const iframe =
            document.querySelector('iframe[src*="/embed/"]') ||
            document.querySelector('iframe[src*="chatflows"]') ||
            document.querySelector('iframe');

          console.log('[SITE COMPLIANCE] Found iframe:', iframe?.src);

          if (iframe?.contentWindow) {
            console.log(
              '[SITE COMPLIANCE] Sending embed:session_update message'
            );
            // DEMO HACK: Use BOHSA Office 3's session ID for Northbridge HQ to share conversation history
            const sessionId = projectId === '59069158-27c4-479a-a68e-b58d6d0a2040'
              ? '85fe49d1-5bec-4a6b-a210-b3edbd8b3069-site'  // Northbridge HQ uses BOHSA Office 3's session
              : `${projectId}-site`;

            iframe.contentWindow.postMessage(
              {
                type: 'embed:session_update',
                user: {
                  id: sessionId,
                  metadata: {
                    embedData
                  }
                }
              },
              '*'
            );
            console.log(
              '[SITE COMPLIANCE] embed:session_update message sent with data'
            );
          } else {
            console.error('[SITE COMPLIANCE] No iframe found!');
          }

          // 2. Register site compliance skills (after session initialized)
          setTimeout(() => {
            console.log('[SITE COMPLIANCE] Registering skills...');

            const siteSkillContext = {
              // Site-specific state setters
              setSiteType,
              setBuildingAge,
              setBuildingOwnership,
              setFloorCount,
              setTotalAreaSqft,
              setFireSystems,
              setElectricalSystems,
              setHvacSystems,
              setWaterSystems,
              setGasSystems,
              setAsbestosStatus,
              setLegionellaRisk,
              setHazardousStorage,
              setFixedEquipment,
              setLiftingEquipment,
              setPressureSystems,
              setPublicAccess,
              setDisabledAccess,
              // Current site values
              siteType,
              buildingAge,
              buildingOwnership,
              floorCount,
              totalAreaSqft,
              fireSystems,
              electricalSystems,
              hvacSystems,
              waterSystems,
              gasSystems,
              asbestosStatus,
              legionellaRisk,
              hazardousStorage,
              fixedEquipment,
              liftingEquipment,
              pressureSystems,
              publicAccess,
              disabledAccess,
              // Common state
              setIsGenerating: setIsSaving,
              projectId,
              siteRequirements: siteRequirements,
              setSiteRequirements: setSiteRequirements,
              siteComplianceService,
              generateSiteRequirementsFromWizard,
              getAllSiteEmbedData
            };

            try {
              registerSiteComplianceSkills(chatFlowsRef, siteSkillContext);
              console.log(
                '[SITE COMPLIANCE] ChatFlows site compliance skills registered'
              );
            } catch (error) {
              console.error(
                '[SITE COMPLIANCE] Error registering skills:',
                error
              );
            }

            // 3. Send initial context (with retry mechanism)
            sendSiteInitialContext(chatFlowsRef, { getAllSiteEmbedData });

            // Send again after 500ms to ensure iframe caught it
            setTimeout(() => {
              sendSiteInitialContext(chatFlowsRef, { getAllSiteEmbedData });
            }, 500);

            // Send again after 1s as final retry
            setTimeout(() => {
              sendSiteInitialContext(chatFlowsRef, { getAllSiteEmbedData });
            }, 1000);
          }, 300); // Wait for session to initialize
        }, 200); // Wait 200ms after clearing
      }, 300); // Longer initial delay
    } else {
      console.warn('[SITE COMPLIANCE] Conditions not met for initialization:', {
        hasChatFlowsRef: !!chatFlowsRef.current,
        hasSpace: !!space
      });
    }
  }, [
    space,
    siteType,
    buildingAge,
    buildingOwnership,
    floorCount,
    totalAreaSqft,
    fireSystems,
    electricalSystems,
    hvacSystems,
    waterSystems,
    gasSystems,
    asbestosStatus,
    legionellaRisk,
    hazardousStorage,
    fixedEquipment,
    liftingEquipment,
    pressureSystems,
    publicAccess,
    disabledAccess,
    siteRequirements,
    projectId,
    generateSiteRequirementsFromWizard,
    setIsSaving,
    getAllSiteEmbedData,
    generateInspections,
    generateCertificates,
    generateDocumentation
  ]);


  const handleChatSubmit = async (messageOverride = null) => {
    const messageToSend = messageOverride || userMessage;
    if (messageToSend.trim()) {
      const openAI = getOpenAI();

      if (!openAI) {
        await addMessages([
          { role: 'user', content: messageToSend },
          {
            role: 'assistant',
            content:
              'Please configure your OpenAI API key to use the chat assistant.'
          }
        ]);
        setUserMessage('');
        // Refocus the input
        setTimeout(() => chatInputRef.current?.focus(), 0);
        return;
      }

      const userMsg = { role: 'user', content: messageToSend };
      await addMessage(userMsg);
      const newMessages = [...messages, userMsg];
      setUserMessage('');
      // Refocus the input
      setTimeout(() => chatInputRef.current?.focus(), 0);
      setIsChatting(true);

      try {
        // Send to AI first to collect the data, then check for section completion
        // This ensures the AI processes the user's response before we move forward
        let result;

        if (wizardMode) {
          // Use wizard chat for collecting information (legacy - not used with ChatFlows)
          const wizardStateForChat = {
            collectedData: {
              company_name: companyName,
              industry: industry,
              location: location,
              team_size: teamSize,
              equipment: equipment,
              risks: risks,
              project_description: projectDescription,
              special_requirements: specialRequirements
            },
            sectionSummaries: {},
            expandedContext: {}
          };
          result = await openAI.wizardChat(newMessages, wizardStateForChat);
        } else {
          // Use regular chat for managing existing requirements
          const context = {
            currentRequirements: siteRequirements
          };
          result = await openAI.chatWithAssistant(newMessages, context);
        }

        if (result.success) {
          const aiResponse = result.response;

          console.log('AI Response:', aiResponse); // Debug logging

          // Process ONLY the primary action - ignore conflicting actions
          let messageContent = '';
          let shouldUpdateState = false;
          let stateUpdate = {};

          if (wizardMode) {
            // Wizard mode - simplified handling with priority order
            // Process section_complete first (highest priority)
            if (aiResponse.action === 'wizard_section_complete') {
              // Map section names from AI to internal names
              const sectionMapping = {
                siteDetails: 'about',
                teamSize: 'workforce',
                risk: 'risk',
                teamVerification: 'teamVerification'
              };

              const internalSection =
                sectionMapping[aiResponse.section] || aiResponse.section;
              const nextSection =
                aiResponse.next_section === 'generate'
                  ? 'confirmation'
                  : aiResponse.next_section;
              const internalNextSection =
                sectionMapping[nextSection] || nextSection;

              console.log('Section completed:', internalSection);

              shouldUpdateState = true;
              stateUpdate = {
                ...wizardState
              };

              messageContent = `✅ ${
                aiResponse.summary || 'Section completed.'
              }\n\n`;

              // Add next section prompt - use internalNextSection which has been mapped
              if (internalNextSection === 'workforce') {
                // Check if we have team context
                if (teamMembers && teamMembers.length > 0) {
                  const departments = [
                    ...new Set(
                      teamMembers.map(m => m.department).filter(Boolean)
                    )
                  ];
                  messageContent += `Now let's talk about your workforce. I can see you have **${teamMembers.length} staff members** registered in your team management system`;
                  if (departments.length > 0) {
                    messageContent += ` across these departments: ${departments.join(
                      ', '
                    )}`;
                  }
                  messageContent += `.\n\nIs this the correct total number of staff for your ${
                    space?.name || 'site'
                  }? If not, just let me know the actual number of employees.`;

                  setSuggestedResponses([
                    {
                      text: `✅ Yes, ${teamMembers.length} is correct`,
                      value: `Yes, we have ${teamMembers.length} employees`
                    },
                    {
                      text: 'Actually less',
                      value: 'We actually have fewer employees than that'
                    },
                    {
                      text: 'Actually more',
                      value: 'We actually have more employees than that'
                    },
                    {
                      text: 'Different number',
                      value: 'Let me give you the correct number'
                    }
                  ]);
                } else {
                  messageContent +=
                    "Now let's move on to understanding your workforce. How many people work at your " +
                    (space?.name || 'site') +
                    '?\n\n💡 **Tip**: You can add team members to the system later through the Team Management tool.';
                  setSuggestedResponses([
                    {
                      text: 'Under 10',
                      value: 'We have less than 10 employees'
                    },
                    {
                      text: '10-50',
                      value: 'We have between 10 and 50 employees'
                    },
                    {
                      text: '50-100',
                      value: 'We have between 50 and 100 employees'
                    },
                    { text: 'Over 100', value: 'We have over 100 employees' }
                  ]);
                }
              } else if (internalNextSection === 'risk') {
                // Contextual suggestions based on industry and what we know
                const isWarehouse =
                  space?.industry?.toLowerCase().includes('warehouse') ||
                  space?.industry?.toLowerCase().includes('distribution') ||
                  space?.name?.toLowerCase().includes('warehouse');

                const hasChemicals =
                  space?.description?.toLowerCase().includes('chemical') ||
                  space?.description?.toLowerCase().includes('bleach') ||
                  space?.description?.toLowerCase().includes('acid') ||
                  wizardState.collectedData?.about?.description
                    ?.toLowerCase()
                    .includes('chemical') ||
                  wizardState.collectedData?.about?.description
                    ?.toLowerCase()
                    .includes('hazardous');

                const hasForklifts = teamMembers?.some(
                  m =>
                    m.department?.toLowerCase().includes('forklift') ||
                    m.role?.toLowerCase().includes('forklift')
                );

                messageContent +=
                  "Excellent! Now let's assess the risks and environmental factors. What are the main safety hazards or risks in your workplace?";

                // Build contextual suggestions
                const suggestions = [];

                if (hasChemicals) {
                  suggestions.push({
                    text: '🧪 Chemical hazards',
                    value:
                      'We handle hazardous chemicals including acids and bleaches requiring COSHH controls'
                  });
                }

                if (isWarehouse || hasForklifts) {
                  suggestions.push({
                    text: '🏗️ Warehouse hazards',
                    value:
                      'Manual handling, forklift operations, working at height on racking, and slip/trip hazards'
                  });
                }

                if (hasForklifts) {
                  suggestions.push({
                    text: '🚜 Vehicle movements',
                    value:
                      'Forklift and vehicle movements in warehouse, pedestrian segregation required'
                  });
                }

                // Always include these options
                suggestions.push({
                  text: '⚠️ Multiple hazards',
                  value:
                    'Chemical handling, manual handling, forklift operations, and working at height'
                });

                if (suggestions.length === 0 || !isWarehouse) {
                  // Fallback to generic options if no context
                  suggestions.push(
                    {
                      text: 'Physical hazards',
                      value:
                        'We have physical hazards like machinery, heights, or manual handling'
                    },
                    {
                      text: 'Chemical hazards',
                      value: 'We work with chemicals or hazardous substances'
                    },
                    {
                      text: 'Environmental',
                      value:
                        'We have environmental risks like noise, temperature, or dust'
                    },
                    {
                      text: 'Low risk office',
                      value: "We're a low-risk office environment"
                    }
                  );
                }

                setSuggestedResponses(suggestions);
              } else if (internalNextSection === 'teamVerification') {
                // Team verification section - check if user corrected team size
                const correctedTeamSize =
                  wizardState.collectedData?.workforce?.teamSize;

                if (
                  correctedTeamSize &&
                  teamMembers &&
                  teamMembers.length > 0
                ) {
                  // User provided a different team size than what's in the system
                  const roleGroups = {};
                  teamMembers.forEach(member => {
                    const key = `${member.role} in ${
                      member.department || 'Unassigned'
                    }`;
                    roleGroups[key] = (roleGroups[key] || 0) + 1;
                  });

                  let teamSummary = `Perfect! You mentioned you have **${correctedTeamSize} employees** total.\n\n`;
                  teamSummary += `I can see ${teamMembers.length} registered in the system:\n`;
                  Object.entries(roleGroups).forEach(([role, count]) => {
                    teamSummary += `• ${count} ${role}\n`;
                  });

                  const difference =
                    parseInt(correctedTeamSize) - teamMembers.length;
                  if (difference > 0) {
                    teamSummary += `\nShould I plan compliance for all ${correctedTeamSize} employees, or would you like to specify the departments and roles for the additional ${difference} staff?`;

                    setSuggestedResponses([
                      {
                        text: `✅ Plan for all ${correctedTeamSize} employees`,
                        value: `Yes, plan for all ${correctedTeamSize} employees`
                      },
                      {
                        text: '📝 Specify other departments',
                        value:
                          'Let me specify the departments for the additional staff'
                      },
                      {
                        text: '✏️ Correct the numbers',
                        value: 'I need to correct these numbers'
                      }
                    ]);
                  } else {
                    teamSummary += '\nIs this correct?';
                    setSuggestedResponses([
                      {
                        text: "✅ Yes, that's correct",
                        value: 'Yes, the team structure is correct'
                      },
                      {
                        text: '✏️ No, let me correct',
                        value: 'The team structure needs correction'
                      }
                    ]);
                  }

                  messageContent += teamSummary;
                } else if (teamMembers && teamMembers.length > 0) {
                  // No corrected team size, show actual team members
                  const roleGroups = {};
                  teamMembers.forEach(member => {
                    const key = `${member.role} in ${
                      member.department || 'Unassigned'
                    }`;
                    roleGroups[key] = (roleGroups[key] || 0) + 1;
                  });

                  let teamSummary =
                    'Perfect! Let me verify your team structure:\n\n';
                  Object.entries(roleGroups).forEach(([role, count]) => {
                    teamSummary += `• ${count} ${role}\n`;
                  });
                  teamSummary += '\nIs this correct?';

                  messageContent += teamSummary;
                  setSuggestedResponses([
                    {
                      text: "✅ Yes, that's correct",
                      value: 'Yes, the team structure is correct'
                    },
                    {
                      text: '✏️ No, let me correct',
                      value: 'The team structure needs correction'
                    }
                  ]);
                } else {
                  messageContent +=
                    'Now let me verify your team details. How many employees do you have in total?';
                  setSuggestedResponses([
                    {
                      text: 'Under 10',
                      value: 'We have less than 10 employees'
                    },
                    {
                      text: '10-50',
                      value: 'We have between 10 and 50 employees'
                    },
                    {
                      text: '50-100',
                      value: 'We have between 50 and 100 employees'
                    },
                    { text: 'Over 100', value: 'We have over 100 employees' }
                  ]);
                }
              } else if (internalNextSection === 'confirmation') {
                messageContent +=
                  'Ready to generate requirements! Is all the information correct?';
                setSuggestedResponses([
                  {
                    text: '✅ Yes, generate requirements',
                    value: 'Yes, generate requirements'
                  },
                  {
                    text: '✏️ Change something',
                    value: 'I need to change some details'
                  }
                ]);
              }
            } else if (aiResponse.action === 'wizard_collect') {
              // Map section names from AI to internal names
              const sectionMapping = {
                siteDetails: 'about',
                teamSize: 'workforce',
                risk: 'risk',
                teamVerification: 'teamVerification'
              };

              // Use the section from AI response if provided, otherwise use current section
              const aiSection =
                aiResponse.section || wizardState.currentSection;
              const sectionToUse =
                sectionMapping[aiSection] || aiSection || 'about';
              const currentSectionData = {
                ...wizardState.collectedData[sectionToUse],
                [aiResponse.field]: aiResponse.value
              };

              // Build summary
              let summary = '';
              Object.entries(currentSectionData).forEach(([key, value]) => {
                if (value) {
                  const label = key
                    .replace(/([A-Z])/g, ' $1')
                    .replace(/_/g, ' ')
                    .trim();
                  summary += `${
                    label.charAt(0).toUpperCase() + label.slice(1)
                  }: ${value}\n`;
                }
              });

              shouldUpdateState = true;
              stateUpdate = {
                collectedData: {
                  ...wizardState.collectedData,
                  [sectionToUse]: currentSectionData
                },
                sectionSummaries: {
                  ...wizardState.sectionSummaries,
                  [sectionToUse]: summary.trim()
                },
                expandedContext: wizardState.expandedContext
              };

              messageContent =
                aiResponse.message || 'Thank you. What else can you tell me?';
            } else if (aiResponse.action === 'generate_requirements') {
              // Generate requirements
              await generateRequirementsFromWizard(aiResponse.collected_data);
              return; // Exit early as generateRequirementsFromWizard handles the message
            } else if (aiResponse.action === 'wizard_ask') {
              // AI is asking a question
              messageContent =
                aiResponse.question || 'Could you provide more details?';

              // Set suggested responses if provided
              if (aiResponse.suggestions && aiResponse.suggestions.length > 0) {
                setSuggestedResponses(
                  aiResponse.suggestions.map(s => ({
                    text: s,
                    value: s
                  }))
                );
              }
            } else if (aiResponse.action === 'wizard_confirm') {
              // AI is confirming collected data
              messageContent =
                aiResponse.message || 'Is all the information correct?';
              setSuggestedResponses([
                {
                  text: '✅ Yes, generate requirements',
                  value: 'Yes, generate requirements'
                },
                {
                  text: '✏️ Change something',
                  value: 'I need to change some details'
                }
              ]);
            } else {
              // Fallback - should not reach here with improved validation
              messageContent =
                'How can I help you with your compliance requirements?';
            }
          } else {
            // Management mode - handle requirement operations
            if (aiResponse.action === 'add') {
              // Add new site requirement to database
              const saveResult = await siteComplianceService.addSiteRequirement(
                projectId,
                {
                  name: aiResponse.qualification.name,
                  description: aiResponse.qualification.description,
                  category: aiResponse.qualification.category,
                  priority: aiResponse.qualification.priority,
                  inspection_frequency: aiResponse.qualification.renewalPeriod,
                  status: 'pending',
                  regulatory_body:
                    aiResponse.qualification.regulatoryBody || '',
                  notes: aiResponse.qualification.notes || ''
                }
              );

              if (saveResult.success) {
                setSiteRequirements(prev => [...prev, saveResult.data]);
                await addMessage({
                  role: 'assistant',
                  content: `✅ Added: ${aiResponse.qualification.name}`
                });
              } else {
                await addMessage({
                  role: 'assistant',
                  content: `❌ Failed to add requirement: ${saveResult.error}`
                });
              }
            } else if (aiResponse.action === 'edit') {
              // Edit existing site requirement
              const updateResult = await siteComplianceService.updateSiteRequirement(
                aiResponse.id,
                aiResponse.changes
              );

              if (updateResult.success) {
                setSiteRequirements(prev =>
                  prev.map(req =>
                    req.id === aiResponse.id ? updateResult.data : req
                  )
                );
                await addMessage({
                  role: 'assistant',
                  content: `✅ Updated site requirement`
                });
              }
            } else if (aiResponse.action === 'warn_delete') {
              // Warning before deleting a required item
              await addMessage({
                role: 'assistant',
                content: aiResponse.message
              });
            } else if (aiResponse.action === 'delete') {
              // Delete site requirement
              const deletedReq = siteRequirements.find(
                q => q.id === aiResponse.id
              );
              const deleteResult = await siteComplianceService.deleteSiteRequirement(
                aiResponse.id
              );

              if (deleteResult.success) {
                setSiteRequirements(prev =>
                  prev.filter(req => req.id !== aiResponse.id)
                );
                await addMessage({
                  role: 'assistant',
                  content: `✅ Removed: ${deletedReq?.name || 'requirement'}`
                });
              }
            } else if (aiResponse.action === 'bulk_add') {
              // Add multiple site requirements
              const saveResult =
                await siteComplianceService.addMultipleSiteRequirements(
                  projectId,
                  aiResponse.qualifications.map(req => ({
                    name: req.name,
                    description: req.description,
                    category: req.category,
                    priority: req.priority,
                    inspection_frequency: req.renewalPeriod,
                    status: 'pending',
                    regulatory_body: req.regulatoryBody || '',
                    notes: req.notes || ''
                  }))
                );

              if (saveResult.success) {
                setSiteRequirements(prev => [...prev, ...saveResult.data]);
                await addMessage({
                  role: 'assistant',
                  content: `✅ Added ${saveResult.data.length} site requirements`
                });
              }
            } else if (aiResponse.action === 'clear_all') {
              // Clear all site requirements
              const deleteResult =
                await siteComplianceService.deleteAllSiteRequirements(projectId);

              if (deleteResult.success) {
                setSiteRequirements([]);
                await addMessage({
                  role: 'assistant',
                  content: `✅ Cleared all site requirements`
                });
              }
            } else if (aiResponse.action === 'update_project') {
              // Update project section with new summary text
              const section = aiResponse.section; // 'about', 'workforce', or 'risk'
              const value = aiResponse.value;

              // Construct updated collected data
              const updatedCollectedData = {
                ...wizardState.collectedData,
                [section]: value
              };

              // Update wizard state with the new section summary
              setWizardState(prev => ({
                ...prev,
                collectedData: updatedCollectedData,
                sectionSummaries: {
                  ...prev.sectionSummaries,
                  [section]: value
                }
              }));

              // Save to database
              await siteComplianceService.saveSiteWizardData(projectId, updatedCollectedData);

              // Update ChatFlows embed with configuration changes - always send all data
              if (chatFlowsRef.current) {
                chatFlowsRef.current.update(getAllSiteEmbedData());
              }

              await addMessage({
                role: 'assistant',
                content: `✅ Updated ${section} section:\n\n${value}`
              });

              // Always show regenerate button when project data changes
              setShowRegenerateButton(true);
            } else {
              // Default management message
              messageContent =
                aiResponse.message ||
                'How can I help you manage your requirements?';
            }
          }

          // Update wizard state if needed
          if (shouldUpdateState) {
            setWizardState(prev => ({
              ...prev,
              ...stateUpdate
            }));
          }

          // Clear old suggested responses unless we're setting new ones
          if (!aiResponse.suggestions && !stateUpdate.currentSection) {
            setSuggestedResponses([]);
          }

          // Always add message if we have content - addMessage will validate
          if (messageContent) {
            await addMessage({
              role: 'assistant',
              content: messageContent
            });
          } else if (!shouldUpdateState) {
            // If no message and no state update, provide fallback
            await addMessage({
              role: 'assistant',
              content: wizardMode
                ? 'Please tell me more about your requirements.'
                : 'How can I help you manage your compliance requirements?'
            });
          }
        } else {
          console.error('AI request failed:', result.error);
          await addMessage({
            role: 'assistant',
            content: `❌ I encountered an error: ${
              result.error || 'Unknown error'
            }. Please try again.`
          });
        }
      } catch (error) {
        console.error('Chat error:', error);
        await addMessage({
          role: 'assistant',
          content: '❌ I encountered an error. Please try again.'
        });
      } finally {
        setIsChatting(false);
        // Refocus the input
        setTimeout(() => chatInputRef.current?.focus(), 100);
      }
    }
  };

  const handleDeleteRequirement = async requirementId => {
    const result = await siteComplianceService.deleteSiteRequirement(requirementId);
    if (result.success) {
      setSiteRequirements(prev => prev.filter(q => q.id !== requirementId));
    }
  };

  const handleClearAllSiteRequirements = () => {
    setShowClearConfirmDialog(true);
  };

  const confirmClearAllSiteRequirements = async () => {
    setShowClearConfirmDialog(false);
    try {
      console.log('🗑️ Clearing all site requirements for project:', projectId);
      const result = await siteComplianceService.deleteAllSiteRequirements(projectId);

      if (result.success) {
        setSiteRequirements([]);
        console.log('✅ All site requirements cleared');
        toast.success('All site requirements cleared', {
          description: `Removed ${siteRequirements.length} requirements`
        });
      } else {
        console.error('❌ Failed to clear site requirements:', result.error);
        toast.error('Failed to clear site requirements', {
          description: result.error
        });
      }
    } catch (error) {
      console.error('❌ Error clearing site requirements:', error);
      toast.error('Error clearing site requirements', {
        description: error.message
      });
    }
  };

  // Handle opening link document dialog
  const handleOpenLinkDialog = (requirement) => {
    setLinkingRequirement(requirement);
    setLinkDialogOpen(true);
  };

  // Handle linking a document to a requirement
  const handleLinkDocument = async (fileInfo) => {
    if (!linkingRequirement) return;

    try {
      // Build version history - preserve old evidence if it exists
      let newVersionHistory = linkingRequirement.evidence_version_history || [];

      if (linkingRequirement.evidence_url) {
        // Add current evidence to version history before replacing
        newVersionHistory = [
          ...newVersionHistory,
          {
            url: linkingRequirement.evidence_url,
            file_id: linkingRequirement.evidence_file_id,
            uploaded_at: linkingRequirement.last_completed_date || new Date().toISOString(),
            uploaded_by: userProfile?.id,
            replaced_at: new Date().toISOString()
          }
        ];
      }

      const result = await siteComplianceService.updateSiteRequirement(linkingRequirement.id, {
        evidence_url: fileInfo.url,
        evidence_file_id: fileInfo.id,
        evidence_version_history: newVersionHistory,
        status: 'compliant',
        last_completed_date: new Date().toISOString()
      });

      if (result.success) {
        // Update local state
        setSiteRequirements(prev =>
          prev.map(req =>
            req.id === linkingRequirement.id
              ? {
                  ...req,
                  evidence_url: fileInfo.url,
                  evidence_file_id: fileInfo.id,
                  evidence_version_history: newVersionHistory,
                  status: 'compliant',
                  last_completed_date: new Date().toISOString()
                }
              : req
          )
        );

        const isReplacement = linkingRequirement.evidence_url;
        toast.success(isReplacement ? 'Document replaced successfully' : 'Document linked successfully', {
          description: `"${fileInfo.name}" linked to "${linkingRequirement.name}"`
        });
      } else {
        toast.error('Failed to link document', {
          description: result.error
        });
      }
    } catch (error) {
      console.error('Error linking document:', error);
      toast.error('Error linking document', {
        description: error.message
      });
    } finally {
      setLinkingRequirement(null);
    }
  };

  // Handle triggering file upload for a requirement
  const handleTriggerUpload = (requirement) => {
    setUploadingRequirement(requirement);
    fileInputRef.current?.click();
  };

  // Handle opening the link task/workflow dialog for checklists
  const handleOpenLinkTaskWorkflowDialog = async (requirement) => {
    setLinkingChecklistRequirement(requirement);
    setLinkTaskWorkflowDialogOpen(true);
    setLoadingTasksWorkflows(true);

    try {
      // Fetch tasks for this space
      const { data: tasks, error: tasksError } = await supabase
        .from('tasks')
        .select('id, name, description, status, due_date, compliance_requirement_id')
        .eq('project_id', projectId)
        .order('created_date', { ascending: false });

      if (tasksError) {
        console.error('Error fetching tasks:', tasksError);
      } else {
        setSpaceTasks(tasks || []);
      }

      // Fetch workflows for this space
      const { data: workflows, error: workflowsError } = await supabase
        .from('workflows')
        .select('id, name, description, status, compliance_requirement_id')
        .eq('project_id', projectId)
        .order('created_date', { ascending: false });

      if (workflowsError) {
        console.error('Error fetching workflows:', workflowsError);
      } else {
        setSpaceWorkflows(workflows || []);
      }
    } catch (error) {
      console.error('Error fetching tasks/workflows:', error);
      toast.error('Failed to load tasks and workflows');
    } finally {
      setLoadingTasksWorkflows(false);
    }
  };

  // Handle linking a task or workflow to a checklist requirement
  const handleLinkTaskWorkflow = async (type, itemId) => {
    if (!linkingChecklistRequirement || !itemId) return;

    try {
      const table = type === 'task' ? 'tasks' : 'workflows';

      // Update the task/workflow with the compliance_requirement_id
      const { error } = await supabase
        .from(table)
        .update({ compliance_requirement_id: linkingChecklistRequirement.id })
        .eq('id', itemId);

      if (error) {
        console.error(`Error linking ${type}:`, error);
        toast.error(`Failed to link ${type}`);
        return;
      }

      toast.success(`${type === 'task' ? 'Task' : 'Workflow'} linked successfully`);
      setLinkTaskWorkflowDialogOpen(false);
      setLinkingChecklistRequirement(null);

      // Refresh the requirements to update UI
      await loadSiteRequirements();
    } catch (error) {
      console.error(`Error linking ${type}:`, error);
      toast.error(`Failed to link ${type}`);
    }
  };

  // Handle file selection and upload
  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !uploadingRequirement) {
      setUploadingRequirement(null);
      return;
    }

    // Reset input so same file can be selected again
    e.target.value = '';

    // Validate file size (25MB max)
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File size must be less than 25MB');
      setUploadingRequirement(null);
      return;
    }

    setIsUploading(true);
    const loadingToast = toast.loading('Uploading document...');

    try {
      // Generate unique file path
      const fileExtension = file.name.split('.').pop().toLowerCase();
      const timestamp = Date.now();
      const sanitizedName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const filePath = `${projectId}/site-compliance/${uploadingRequirement.id}/${timestamp}_${sanitizedName}`;

      // Upload to GCS
      const uploadResult = await gcsUploadFile(file, {
        folderPath: `project-files/${projectId}/site-compliance/${uploadingRequirement.id}`,
        fileName: `${timestamp}_${sanitizedName}`,
        contentType: file.type,
      });

      const publicUrl = uploadResult.publicUrl;

      // Build version history - preserve old evidence if it exists
      let newVersionHistory = uploadingRequirement.evidence_version_history || [];

      if (uploadingRequirement.evidence_url) {
        // Add current evidence to version history before replacing
        newVersionHistory = [
          ...newVersionHistory,
          {
            url: uploadingRequirement.evidence_url,
            file_id: uploadingRequirement.evidence_file_id,
            file_name: uploadingRequirement.evidence_file_name,
            uploaded_at: uploadingRequirement.last_completed_date || new Date().toISOString(),
            uploaded_by: userProfile?.id,
            replaced_at: new Date().toISOString()
          }
        ];
      }

      // Also create a record in project_files table for the docs tool
      const { data: fileRecord, error: fileError } = await supabase
        .from('project_files')
        .insert({
          project_id: projectId,
          folder_id: null,
          name: file.name,
          file_path: publicUrl,
          file_size: file.size,
          file_type: fileExtension,
          uploaded_by: userProfile?.id
        })
        .select('id')
        .single();

      if (fileError) {
        console.error('Error creating file record:', fileError);
        // Continue anyway - the file is uploaded, just won't show in docs tool
      }

      // Update site_compliance_requirements with new evidence
      const result = await siteComplianceService.updateSiteRequirement(uploadingRequirement.id, {
        evidence_url: publicUrl,
        evidence_file_id: fileRecord?.id || null,
        evidence_file_name: file.name,
        evidence_version_history: newVersionHistory,
        status: 'compliant',
        last_completed_date: new Date().toISOString()
      });

      if (result.success) {
        // Update local state
        setSiteRequirements(prev =>
          prev.map(req =>
            req.id === uploadingRequirement.id
              ? {
                  ...req,
                  evidence_url: publicUrl,
                  evidence_file_id: fileRecord?.id || null,
                  evidence_file_name: file.name,
                  evidence_version_history: newVersionHistory,
                  status: 'compliant',
                  last_completed_date: new Date().toISOString()
                }
              : req
          )
        );

        const isReplacement = uploadingRequirement.evidence_url;
        toast.success(isReplacement ? 'New version uploaded' : 'Document uploaded successfully', {
          id: loadingToast,
          description: `"${file.name}" linked to "${uploadingRequirement.name}"`
        });
      } else {
        toast.error('Failed to update requirement', { id: loadingToast });
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      toast.error('Error uploading document', {
        id: loadingToast,
        description: error.message
      });
    } finally {
      setIsUploading(false);
      setUploadingRequirement(null);
    }
  };

  // Handle approving a site requirement
  const handleApproveRequirement = async (requirementId) => {
    const approverName = userProfile?.full_name ||
      `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() ||
      userProfile?.email;
    const approvalData = {
      approved_by: userProfile?.id,
      approved_by_name: approverName,
      approved_at: new Date().toISOString()
    };

    const result = await siteComplianceService.updateSiteRequirement(requirementId, {
      approval_status: 'approved',
      approval_data: approvalData
    });

    if (result.success) {
      setSiteRequirements(prev =>
        prev.map(r =>
          r.id === requirementId
            ? { ...r, approval_status: 'approved', approval_data: approvalData }
            : r
        )
      );
      toast.success('Requirement approved');
    } else {
      toast.error('Failed to approve requirement');
    }
  };

  // Handle rejecting a site requirement
  const handleRejectRequirement = async (requirementId) => {
    const rejecterName = userProfile?.full_name ||
      `${userProfile?.first_name || ''} ${userProfile?.last_name || ''}`.trim() ||
      userProfile?.email;
    const approvalData = {
      rejected_by: userProfile?.id,
      rejected_by_name: rejecterName,
      rejected_at: new Date().toISOString()
    };

    const result = await siteComplianceService.updateSiteRequirement(requirementId, {
      approval_status: 'rejected',
      approval_data: approvalData
    });

    if (result.success) {
      setSiteRequirements(prev =>
        prev.map(r =>
          r.id === requirementId
            ? { ...r, approval_status: 'rejected', approval_data: approvalData }
            : r
        )
      );
      toast.success('Requirement rejected');
    } else {
      toast.error('Failed to reject requirement');
    }
  };

  const handleEditRequirement = requirement => {
    setEditingRequirement(requirement);
    setEditFormData({
      name: requirement.name || '',
      description: requirement.description || '',
      category: requirement.category || 'Fire Safety',
      priority: requirement.priority || 'medium',
      inspection_frequency: requirement.inspection_frequency || '',
      regulatory_body: requirement.regulatory_body || '',
      regulation_reference: requirement.regulation_reference || '',
      legal_requirement: requirement.legal_requirement || false,
      notes: requirement.notes || '',
      status: requirement.status || 'pending'
    });
  };

  const handleSaveEdit = async () => {
    if (!editingRequirement) return;

    setIsSaving(true);
    const result = await siteComplianceService.updateSiteRequirement(
      editingRequirement.id,
      editFormData
    );

    if (result.success) {
      setSiteRequirements(prev =>
        prev.map(q =>
          q.id === editingRequirement.id ? { ...q, ...editFormData } : q
        )
      );
      setEditingRequirement(null);
      setEditFormData({});
    }
    setIsSaving(false);
  };

  // Dynamic generation progress over 90 seconds
  useEffect(() => {
    if (!isGenerating) {
      // Reset to initial state when not generating
      setGenerationProgress({
        stage: 0,
        message: 'Initializing AI analysis...',
        substeps: []
      });
      return;
    }

    // Define progress stages with timing (in seconds)
    const stages = [
      {
        time: 0,
        stage: 0,
        message: 'Initializing AI analysis...',
        substeps: ['Connecting to compliance engine', 'Loading industry data']
      },
      {
        time: 8,
        stage: 1,
        message: 'Researching industry regulations...',
        substeps: ['Analyzing sector-specific requirements', 'Reviewing legal frameworks', 'Identifying mandatory standards']
      },
      {
        time: 20,
        stage: 2,
        message: 'Analyzing your business context...',
        substeps: ['Processing team structure', 'Evaluating work environment', 'Assessing operational risks']
      },
      {
        time: 35,
        stage: 3,
        message: 'Identifying compliance requirements...',
        substeps: ['Matching regulations to your operations', 'Prioritizing critical requirements', 'Building requirement library']
      },
      {
        time: 52,
        stage: 4,
        message: 'Generating training courses...',
        substeps: ['Creating mandatory training programs', 'Assigning certification requirements', 'Setting renewal periods']
      },
      {
        time: 68,
        stage: 5,
        message: 'Creating checklists and documentation...',
        substeps: ['Designing inspection checklists', 'Generating policy templates', 'Building audit frameworks']
      },
      {
        time: 82,
        stage: 6,
        message: 'Finalizing requirements...',
        substeps: ['Validating compliance coverage', 'Optimizing requirement structure', 'Preparing recommendations']
      }
    ];

    let currentStage = 0;
    const startTime = Date.now();

    const updateProgress = () => {
      const elapsed = (Date.now() - startTime) / 1000; // seconds

      // Find the current stage based on elapsed time
      for (let i = stages.length - 1; i >= 0; i--) {
        if (elapsed >= stages[i].time) {
          if (currentStage !== stages[i].stage) {
            currentStage = stages[i].stage;
            setGenerationProgress(stages[i]);
          }
          break;
        }
      }
    };

    // Update progress every 500ms
    const interval = setInterval(updateProgress, 500);

    // Initial update
    updateProgress();

    return () => clearInterval(interval);
  }, [isGenerating]);

  // Memoized helper function for priority colors
  const getPriorityColor = useCallback(priority => {
    switch (priority) {
      case 'high':
        return 'destructive';
      case 'medium':
        return 'secondary';
      case 'low':
        return 'outline';
      default:
        return 'outline';
    }
  }, []); // No dependencies, this function is pure

  const getStatusIcon = status => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className='h-4 w-4 text-green-600' />;
      case 'expired':
        return <XCircle className='h-4 w-4 text-red-600' />;
      case 'pending':
        return <Clock className='h-4 w-4 text-yellow-600' />;
      default:
        return <Info className='h-4 w-4 text-gray-600' />;
    }
  };

  const getCategoryIcon = (category, requirementType) => {
    // Check requirement_type first for specific icons
    if (requirementType === 'internal_checklist') {
      return <ListChecks className='h-4 w-4' />;
    }
    if (requirementType === 'course') {
      return <Award className='h-4 w-4' />;
    }

    // Otherwise use category-based icons
    switch (category) {
      case 'Equipment':
        return <Briefcase className='h-4 w-4' />;
      case 'Safety':
        return <Shield className='h-4 w-4' />;
      case 'Compliance':
        return <FileCheck className='h-4 w-4' />;
      case 'Training':
        return <Users className='h-4 w-4' />;
      case 'Environmental':
        return <MapPin className='h-4 w-4' />;
      default:
        return <FileText className='h-4 w-4' />;
    }
  };

  // Smart matching function to match requirements to team members - memoized
  const getMatchingTeamMembers = useCallback(
    requirement => {
      const personnel = (requirement.minimum_personnel || '').toLowerCase();

      // Check for "all staff" patterns
      if (
        personnel.includes('all staff') ||
        personnel.includes('100%') ||
        personnel.includes('entire workforce') ||
        personnel.includes('all employees')
      ) {
        return teamMembers;
      }

      // Check for factory workers
      if (personnel.includes('factory worker')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('factory') ||
            m.role?.toLowerCase().includes('factory')
        );
      }

      // Check for managers/supervisors
      if (personnel.includes('manager') || personnel.includes('supervisor')) {
        return teamMembers.filter(
          m =>
            m.role?.toLowerCase().includes('manager') ||
            m.role?.toLowerCase().includes('supervisor')
        );
      }

      // Check for forklift operators
      if (personnel.includes('forklift')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('forklift') ||
            m.role?.toLowerCase().includes('forklift') ||
            m.department?.toLowerCase().includes('warehouse') ||
            m.department?.toLowerCase().includes('logistics')
        );
      }

      // Check for maintenance workers
      if (personnel.includes('maintenance')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('maintenance') ||
            m.role?.toLowerCase().includes('maintenance')
        );
      }

      // Check for janitors/cleaners
      if (personnel.includes('janitor') || personnel.includes('cleaner')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('janitor') ||
            m.role?.toLowerCase().includes('janitor') ||
            m.department?.toLowerCase().includes('cleaning')
        );
      }

      // Check for office/admin staff
      if (personnel.includes('office') || personnel.includes('admin')) {
        return teamMembers.filter(
          m =>
            m.department?.toLowerCase().includes('office') ||
            m.role?.toLowerCase().includes('admin') ||
            m.department?.toLowerCase().includes('marketing')
        );
      }

      // Check for ratio-based requirements (e.g., "1 per 20 employees")
      if (personnel.includes('per') && personnel.includes('employee')) {
        // For ratio-based, suggest managers or senior staff
        const managers = teamMembers.filter(
          m =>
            m.role?.toLowerCase().includes('manager') ||
            m.role?.toLowerCase().includes('supervisor')
        );

        // If we have managers, return them, otherwise return a subset of all staff
        if (managers.length > 0) {
          return managers;
        }

        // Calculate how many we need based on the ratio
        const match = personnel.match(/(\d+)\s*per\s*(\d+)/i);
        if (match) {
          const needed =
            Math.ceil(teamMembers.length / parseInt(match[2])) *
            parseInt(match[1]);
          return teamMembers.slice(0, needed);
        }
      }

      // Default: if we can't match specifically, return empty array
      return [];
    },
    [teamMembers]
  ); // Only recreate if teamMembers changes

  // Calculate site compliance wizard progress based on key site info fields
  const wizardProgress =
    (((siteType ? 1 : 0) +
      (buildingAge ? 1 : 0) +
      (fireSystems ? 1 : 0) +
      (electricalSystems ? 1 : 0) +
      (waterSystems ? 1 : 0) +
      (asbestosStatus ? 1 : 0)) /
      6) *
    100;

  if (isLoading) {
    return (
      <AppShell3>
        <div className='flex items-center justify-center h-64'>
          <Loader2 className='h-8 w-8 animate-spin text-primary' />
        </div>
      </AppShell3>
    );
  }

  return (
    <PageTransition>
      <AppShell3>
        <>
          {/* Hidden file input for document uploads */}
          <input
            ref={fileInputRef}
            type="file"
            className="hidden"
            onChange={handleFileUpload}
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.gif,.txt,.csv"
          />

          {/* ToolHeader with custom actions */}
          <ToolHeader
            spaceName={space?.name || 'Space'}
            toolName='AI Compliance'
            currentTool='compliance'
            spaceTools={spaceTools}
            actions={
              <>
                {/* Filter Dropdown */}
                {siteRequirements.length > 0 && (
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant='outline'
                        size='sm'
                        className={cn(
                          'gap-2',
                          activeFilters.length < 3 && 'bg-violet-50 border-violet-200 text-violet-700'
                        )}
                      >
                        <Filter className='h-4 w-4' />
                        Filters
                        {activeFilters.length < 3 && (
                          <Badge variant='secondary' className='ml-1 h-5 px-1.5 text-xs bg-violet-100 text-violet-700'>
                            {activeFilters.length}
                          </Badge>
                        )}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className='w-56 p-3' align='end'>
                      <div className='space-y-3'>
                        <p className='text-sm font-medium text-gray-700'>Filter by type</p>
                        <div className='space-y-2'>
                          <label className='flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded p-1.5 -mx-1.5 transition-colors'>
                            <Checkbox
                              checked={activeFilters.includes('legal')}
                              onCheckedChange={() => toggleFilter('legal')}
                            />
                            <div className='flex items-center gap-2'>
                              <Shield className='h-4 w-4 text-amber-600' />
                              <span className='text-sm'>Legal</span>
                            </div>
                          </label>
                          <label className='flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded p-1.5 -mx-1.5 transition-colors'>
                            <Checkbox
                              checked={activeFilters.includes('document')}
                              onCheckedChange={() => toggleFilter('document')}
                            />
                            <div className='flex items-center gap-2'>
                              <FileText className='h-4 w-4 text-purple-600' />
                              <span className='text-sm'>Document</span>
                            </div>
                          </label>
                          <label className='flex items-center gap-2 cursor-pointer hover:bg-gray-50 rounded p-1.5 -mx-1.5 transition-colors'>
                            <Checkbox
                              checked={activeFilters.includes('checklist')}
                              onCheckedChange={() => toggleFilter('checklist')}
                            />
                            <div className='flex items-center gap-2'>
                              <ListChecks className='h-4 w-4 text-blue-600' />
                              <span className='text-sm'>Checklist</span>
                            </div>
                          </label>
                        </div>
                        {activeFilters.length < 3 && (
                          <Button
                            variant='ghost'
                            size='sm'
                            className='w-full text-xs h-7'
                            onClick={() => setActiveFilters(['legal', 'document', 'checklist'])}
                          >
                            Show all
                          </Button>
                        )}
                      </div>
                    </PopoverContent>
                  </Popover>
                )}
                {/* Clear Button - localhost only */}
                {window.location.hostname === 'localhost' && siteRequirements.length > 0 && (
                  <Button
                    variant='outline'
                    size='sm'
                    onClick={handleClearAllSiteRequirements}
                    className='text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200'
                  >
                    <Trash2 className='h-4 w-4 mr-2' />
                    Clear ({siteRequirements.length})
                  </Button>
                )}
              </>
            }
          />

          {/* Main Content - GPT Builder Style Layout */}
          <div className='w-full h-[calc(100vh-8rem)] bg-white'>
            <div className='flex h-full border-t border-gray-200'>
              {/* Left Panel - Main Content */}
              <div className='flex-1 bg-white flex flex-col overflow-hidden border-r border-gray-200 relative'>
                {/* Generation Settings Overlay */}
                <div className='absolute top-1.5 right-3 z-10 pointer-events-none'>
                  <motion.div
                    className='bg-white shadow-lg rounded-lg pointer-events-auto border border-gray-200 overflow-hidden'
                    initial={false}
                    animate={{
                      width: settingsExpanded ? 'auto' : 'auto'
                    }}
                    transition={{
                      type: 'spring',
                      stiffness: 300,
                      damping: 30
                    }}
                  >
                    <div className='flex items-center gap-3 px-3 py-2 flex-row-reverse'>
                      {/* Settings Icon Button */}
                      <motion.button
                        onClick={() => setSettingsExpanded(!settingsExpanded)}
                        className='flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 transition-colors flex-shrink-0'
                        animate={{
                          rotate: settingsExpanded ? -180 : 0
                        }}
                        transition={{
                          type: 'spring',
                          stiffness: 200,
                          damping: 20
                        }}
                      >
                        <Settings className='h-4 w-4 text-gray-600' />
                      </motion.button>

                      {/* Expandable Toggle Options */}
                      <AnimatePresence>
                        {settingsExpanded && (
                          <motion.div
                            initial={{ opacity: 0, width: 0 }}
                            animate={{ opacity: 1, width: 'auto' }}
                            exit={{ opacity: 0, width: 0 }}
                            transition={{ duration: 0.2 }}
                            className='flex items-center gap-3 overflow-hidden'
                          >
                            <motion.div
                              className='flex items-center gap-1.5'
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.2 }}
                            >
                              <Switch
                                id='gen-documentation'
                                checked={generateDocumentation}
                                onCheckedChange={(value) => {
                                  console.log('🔀 Toggle Documentation:', value);
                                  setGenerateDocumentation(value);
                                }}
                              />
                              <Label
                                htmlFor='gen-documentation'
                                className='text-xs text-gray-700 cursor-pointer whitespace-nowrap'
                              >
                                Documentation
                              </Label>
                            </motion.div>
                            <motion.div
                              className='flex items-center gap-1.5'
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.15 }}
                            >
                              <Switch
                                id='gen-inspections'
                                checked={generateInspections}
                                onCheckedChange={(value) => {
                                  console.log('🔀 Toggle Inspections:', value);
                                  setGenerateInspections(value);
                                }}
                              />
                              <Label
                                htmlFor='gen-inspections'
                                className='text-xs text-gray-700 cursor-pointer whitespace-nowrap'
                              >
                                Inspections
                              </Label>
                            </motion.div>
                            <motion.div
                              className='flex items-center gap-1.5'
                              initial={{ opacity: 0, x: 10 }}
                              animate={{ opacity: 1, x: 0 }}
                              transition={{ delay: 0.1 }}
                            >
                              <Switch
                                id='gen-certificates'
                                checked={generateCertificates}
                                onCheckedChange={(value) => {
                                  console.log('🔀 Toggle Certificates:', value);
                                  setGenerateCertificates(value);
                                }}
                              />
                              <Label
                                htmlFor='gen-certificates'
                                className='text-xs text-gray-700 cursor-pointer whitespace-nowrap'
                              >
                                Certificates
                              </Label>
                            </motion.div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      {/* Always visible label */}
                      <span className='text-xs font-medium text-gray-700 whitespace-nowrap'>
                        Generation Settings
                      </span>
                    </div>
                  </motion.div>
                </div>

                {/* ChatFlows Embed - Full height */}
                <ChatFlowsEmbed
                  key={projectId}
                  ref={chatFlowsRef}
                  publicKey='5cc9d9bbd7dfacca'
                  apiUrl='https://chatflows.add.ai'
                  inline={true}
                  className='flex-1'
                  style={{ height: '100%' }}
                  showClose={false}
                  showExpand={false}
                  showTranscript={false}
                  disableRounding={true}
                  deferInit={true}
                  onReady={handleChatFlowsReady}
                />
              </div>

              {/* Right Panel - Preview */}
              <div className='flex-1 bg-gray-50 flex flex-col overflow-hidden relative'>
                {/* Preview Content */}
                <div className='flex-1 overflow-y-auto px-6 pt-6 pb-16 bg-gray-50'>
                  {isGenerating ? (
                    /* Loading state while generating requirements */
                    <div className='flex flex-col items-center justify-center h-full'>
                      <div className='text-center max-w-md'>
                        {/* Animated spinner with gradient */}
                        <div className='relative mb-6'>
                          <div className='absolute inset-0 flex items-center justify-center'>
                            <div className='h-16 w-16 rounded-full bg-gradient-to-tr from-purple-600 via-blue-500 to-green-500 animate-pulse opacity-20'></div>
                          </div>
                          <Loader2 className='h-12 w-12 animate-spin text-purple-600 mx-auto relative' />
                        </div>

                        <p className='text-gray-900 text-base font-semibold mb-2'>
                          {generationProgress.message}
                        </p>
                        <p className='text-gray-600 text-sm mb-4'>
                          Our AI is reviewing your business information and generating tailored compliance requirements...
                        </p>

                        {/* Dynamic progress steps */}
                        <div className='mt-6 space-y-2.5 text-left'>
                          {generationProgress.substeps.map((step, index) => (
                            <div key={index} className='flex items-center gap-3 text-xs text-gray-700'>
                              <div className='h-1.5 w-1.5 rounded-full bg-purple-600 animate-pulse'
                                   style={{ animationDelay: `${index * 0.2}s` }}></div>
                              <span>{step}</span>
                            </div>
                          ))}
                        </div>

                        {/* Progress bar */}
                        <div className='mt-8 w-full bg-gray-200 rounded-full h-1.5 overflow-hidden'>
                          <div
                            className='h-full bg-gradient-to-r from-purple-600 to-blue-500 transition-all duration-1000 ease-out'
                            style={{ width: `${(generationProgress.stage / 6) * 100}%` }}
                          ></div>
                        </div>

                        <p className='text-gray-400 text-xs mt-4'>
                          Stage {generationProgress.stage + 1} of 7 • Typically takes 60-90 seconds
                        </p>
                      </div>
                    </div>
                  ) : siteRequirements.length > 0 ? (
                    /* Show requirements list grouped by type */
                    <div className='px-4 pt-2 pb-6'>
                      {(() => {
                        // Group FILTERED requirements by category for site compliance
                        const grouped = filteredSiteRequirements.reduce((acc, req) => {
                          const category = req.category || 'Other';
                          if (!acc[category]) acc[category] = [];
                          acc[category].push(req);
                          return acc;
                        }, {});

                        // Helper to get category-specific styling
                        const getCategoryIcon = (category) => {
                          switch(category) {
                            case 'Fire Safety':
                              return <Flame className='h-4 w-4 text-red-600' />;
                            case 'Electrical':
                              return <Zap className='h-4 w-4 text-yellow-600' />;
                            case 'Gas Safety':
                              return <Flame className='h-4 w-4 text-orange-600' />;
                            case 'Water Hygiene':
                              return <Droplets className='h-4 w-4 text-blue-600' />;
                            case 'Asbestos':
                              return <AlertTriangle className='h-4 w-4 text-purple-600' />;
                            case 'Lifting Equipment (LOLER)':
                              return <ArrowUpDown className='h-4 w-4 text-indigo-600' />;
                            case 'Pressure Systems (PSSR)':
                              return <Gauge className='h-4 w-4 text-cyan-600' />;
                            case 'Structural':
                              return <Building className='h-4 w-4 text-stone-600' />;
                            case 'Environmental':
                              return <Leaf className='h-4 w-4 text-green-600' />;
                            case 'Insurance':
                              return <Shield className='h-4 w-4 text-emerald-600' />;
                            case 'Access and DDA':
                              return <Accessibility className='h-4 w-4 text-teal-600' />;
                            case 'Security':
                              return <Lock className='h-4 w-4 text-gray-600' />;
                            default:
                              return <FileText className='h-4 w-4 text-gray-600' />;
                          }
                        };

                        const getCategoryLabel = (category) => {
                          return category;
                        };

                        // If no filtered requirements, show empty state
                        if (filteredSiteRequirements.length === 0) {
                          return (
                            <div className='flex flex-col items-center justify-center py-12 px-4'>
                              <Filter className='h-12 w-12 text-gray-300 mb-3' />
                              <p className='text-gray-500 text-sm text-center'>
                                No requirements match your current filters.
                                <br />
                                Try adjusting your filter settings.
                              </p>
                              <Button
                                variant='outline'
                                size='sm'
                                className='mt-4'
                                onClick={() => setActiveFilters(['legal', 'document', 'checklist'])}
                              >
                                Show all requirements
                              </Button>
                            </div>
                          );
                        }

                        return (
                          <Accordion
                            type="multiple"
                            defaultValue={Object.keys(grouped)}
                            className='space-y-3'
                          >
                            {Object.entries(grouped).map(([category, items]) => (
                              <AccordionItem
                                key={category}
                                value={category}
                                className='border rounded-lg bg-white shadow-sm'
                              >
                                <AccordionTrigger className='px-4 py-3 hover:no-underline hover:bg-gray-50 transition-colors'>
                                  <div className='flex items-center gap-3 w-full'>
                                    {getCategoryIcon(category)}
                                    <span className='font-semibold text-sm text-gray-900'>
                                      {getCategoryLabel(category)}
                                    </span>
                                    <Badge variant='secondary' className='text-xs ml-auto mr-2'>
                                      {items.length} {items.length === 1 ? 'item' : 'items'}
                                    </Badge>
                                  </div>
                                </AccordionTrigger>
                                <AccordionContent className='px-0 pb-0'>
                                  <div className='divide-y divide-gray-100'>
                                    {items.map((qual, index) => (
                                      <div
                                        key={qual.id}
                                        className={cn(
                                          'px-4 py-3 hover:bg-gray-50 transition-colors cursor-pointer group',
                                          selectedRequirement?.id === qual.id && 'bg-blue-50 hover:bg-blue-50'
                                        )}
                                        onClick={() => setSelectedRequirement(qual)}
                                      >
                                        {/* Header row */}
                                        <div className='flex items-start justify-between gap-3 mb-2'>
                                          <div className='flex items-start gap-2 flex-1 min-w-0'>
                                            <div className='mt-0.5'>
                                              {getCategoryIcon(qual.category, qual.requirement_type)}
                                            </div>
                                            <div className='flex-1 min-w-0'>
                                              <h4 className='text-sm font-medium text-gray-900 mb-0.5'>
                                                {qual.name}
                                              </h4>
                                              {qual.description && (
                                                <p className='text-xs text-gray-600 line-clamp-2'>
                                                  {qual.description}
                                                </p>
                                              )}
                                            </div>
                                          </div>
                                          <div className='flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity'>
                                            <Button
                                              variant='ghost'
                                              size='icon'
                                              className='h-7 w-7'
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleEditRequirement(qual);
                                              }}
                                            >
                                              <Edit className='h-3 w-3' />
                                            </Button>
                                            <Button
                                              variant='ghost'
                                              size='icon'
                                              className='h-7 w-7 text-red-600 hover:text-red-700 hover:bg-red-50'
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleDeleteRequirement(qual.id);
                                              }}
                                            >
                                              <Trash2 className='h-3 w-3' />
                                            </Button>
                                          </div>
                                        </div>

                                        {/* Badges row */}
                                        <div className='flex flex-wrap items-center gap-1.5 ml-6'>
                                          <Badge
                                            variant={getPriorityColor(qual.priority)}
                                            className='text-xs'
                                          >
                                            {qual.priority}
                                          </Badge>
                                          {/* Only show required counts and renewal for courses, not checklists or documentation */}
                                          {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && qual.calculated_required && (
                                            <Badge variant='secondary' className='text-xs'>
                                              <Users className='h-3 w-3 mr-1' />
                                              {qual.calculated_required}{' '}
                                              {qual.calculated_required === 1 ? 'person' : 'people'}
                                            </Badge>
                                          )}
                                          {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && qual.recommended_required && (
                                            <Badge variant='outline' className='text-xs bg-blue-50 text-blue-700 border-blue-200'>
                                              <Users className='h-3 w-3 mr-1' />
                                              {qual.recommended_required}{' '}
                                              {qual.recommended_required === 1 ? 'person' : 'people'} rec.
                                            </Badge>
                                          )}
                                          {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && qual.renewal_period && (
                                            <Badge variant='outline' className='text-xs'>
                                              <Clock className='h-3 w-3 mr-1' />
                                              {qual.renewal_period}
                                            </Badge>
                                          )}
                                          {qual.legal_requirement && (
                                            <Badge variant='outline' className='text-xs border-amber-300 text-amber-700 bg-amber-50'>
                                              <Shield className='h-3 w-3 mr-1' />
                                              Legal
                                            </Badge>
                                          )}
                                          {qual.requirement_type === 'documentation' && (
                                            <Badge variant='outline' className='text-xs border-purple-300 text-purple-700 bg-purple-50'>
                                              <FileText className='h-3 w-3 mr-1' />
                                              Document
                                            </Badge>
                                          )}
                                          {qual.requirement_type === 'internal_checklist' && (
                                            <Badge variant='outline' className='text-xs border-blue-300 text-blue-700 bg-blue-50'>
                                              <ListChecks className='h-3 w-3 mr-1' />
                                              Checklist
                                            </Badge>
                                          )}
                                          {/* Linked document indicator */}
                                          {qual.evidence_url && (
                                            <Badge variant='outline' className='text-xs border-green-300 text-green-700 bg-green-50'>
                                              <Check className='h-3 w-3 mr-1' />
                                              Linked
                                            </Badge>
                                          )}
                                          {getStatusIcon(qual.status)}
                                        </div>

                                        {/* Regulatory info */}
                                        {qual.regulatory_body && (
                                          <div className='mt-2 ml-6 text-xs text-gray-500'>
                                            {qual.regulatory_body}
                                            {qual.regulation_reference && ` • ${qual.regulation_reference}`}
                                          </div>
                                        )}

                                        {/* Actions and approval row */}
                                        <div className='mt-3 ml-6 flex items-center justify-between gap-2'>
                                          {/* Left side - action buttons (only show when approved) */}
                                          <div className='flex gap-2'>
                                            {qual.approval_status === 'approved' && (
                                              <>
                                                {/* Checklist actions */}
                                                {qual.requirement_type === 'internal_checklist' && (
                                                  <>
                                                    {qual.evidence_url ? (
                                                      <>
                                                        <Button
                                                          variant='outline'
                                                          size='sm'
                                                          className='h-7 text-xs border-green-200 text-green-700 hover:bg-green-50'
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(qual.evidence_url, '_blank');
                                                          }}
                                                        >
                                                          <ExternalLink className='h-3 w-3 mr-1' />
                                                          View
                                                        </Button>
                                                        <Button
                                                          variant='outline'
                                                          size='sm'
                                                          className='h-7 text-xs'
                                                          disabled={isUploading}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleTriggerUpload(qual);
                                                          }}
                                                        >
                                                          <Upload className='h-3 w-3 mr-1' />
                                                          Upload
                                                        </Button>
                                                      </>
                                                    ) : (
                                                      <Button
                                                        variant='outline'
                                                        size='sm'
                                                        className='h-7 text-xs'
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleOpenLinkTaskWorkflowDialog(qual);
                                                        }}
                                                      >
                                                        <Link className='h-3 w-3 mr-1' />
                                                        Link Task/Workflow
                                                      </Button>
                                                    )}
                                                  </>
                                                )}

                                                {/* Documentation actions */}
                                                {qual.requirement_type === 'documentation' && (
                                                  <>
                                                    {qual.evidence_url ? (
                                                      <>
                                                        <Button
                                                          variant='outline'
                                                          size='sm'
                                                          className='h-7 text-xs border-green-200 text-green-700 hover:bg-green-50'
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            window.open(qual.evidence_url, '_blank');
                                                          }}
                                                        >
                                                          <ExternalLink className='h-3 w-3 mr-1' />
                                                          View
                                                        </Button>
                                                        <Button
                                                          variant='outline'
                                                          size='sm'
                                                          className='h-7 text-xs'
                                                          disabled={isUploading}
                                                          onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleTriggerUpload(qual);
                                                          }}
                                                        >
                                                          <Upload className='h-3 w-3 mr-1' />
                                                          Upload
                                                        </Button>
                                                      </>
                                                    ) : (
                                                      <Button
                                                        variant='outline'
                                                        size='sm'
                                                        className='h-7 text-xs'
                                                        disabled={isUploading}
                                                        onClick={(e) => {
                                                          e.stopPropagation();
                                                          handleTriggerUpload(qual);
                                                        }}
                                                      >
                                                        <Upload className='h-3 w-3 mr-1' />
                                                        Upload
                                                      </Button>
                                                    )}
                                                  </>
                                                )}

                                                {/* Default actions for other requirement types */}
                                                {qual.requirement_type !== 'internal_checklist' && qual.requirement_type !== 'documentation' && (
                                                  <>
                                                    <Button
                                                      variant='outline'
                                                      size='sm'
                                                      className='h-7 text-xs'
                                                      onClick={(e) => {
                                                        e.stopPropagation();
                                                        console.log('Upload evidence:', qual.id);
                                                      }}
                                                    >
                                                      <Upload className='h-3 w-3 mr-1' />
                                                      Upload Evidence
                                                    </Button>
                                                  </>
                                                )}
                                              </>
                                            )}
                                          </div>

                                          {/* Right side - approval buttons */}
                                          <div className='flex items-center gap-2'>
                                            {/* Info icon with tooltip showing who approved/rejected */}
                                            {(qual.approval_status === 'approved' || qual.approval_status === 'rejected') && qual.approval_data && (
                                              <TooltipProvider>
                                                <Tooltip>
                                                  <TooltipTrigger asChild>
                                                    <div className='h-7 w-7 flex items-center justify-center cursor-help'>
                                                      <Info className='h-4 w-4 text-gray-400' />
                                                    </div>
                                                  </TooltipTrigger>
                                                  <TooltipContent>
                                                    <p className='text-xs'>
                                                      {qual.approval_status === 'approved' ? 'Approved' : 'Rejected'} by {qual.approval_data.approved_by_name || qual.approval_data.rejected_by_name}
                                                    </p>
                                                  </TooltipContent>
                                                </Tooltip>
                                              </TooltipProvider>
                                            )}
                                            <Button
                                              variant={qual.approval_status === 'approved' ? 'default' : 'outline'}
                                              size='sm'
                                              className={cn(
                                                'h-7 text-xs min-w-[90px] justify-center',
                                                qual.approval_status === 'approved'
                                                  ? 'bg-green-600 hover:bg-green-700 text-white'
                                                  : 'text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700'
                                              )}
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleApproveRequirement(qual.id);
                                              }}
                                            >
                                              <CheckCircle className='h-3 w-3 mr-1' />
                                              {qual.approval_status === 'approved' ? 'Approved' : 'Approve'}
                                            </Button>
                                            <Button
                                              variant={qual.approval_status === 'rejected' ? 'default' : 'outline'}
                                              size='sm'
                                              className={cn(
                                                'h-7 text-xs min-w-[85px] justify-center',
                                                qual.approval_status === 'rejected'
                                                  ? 'bg-red-600 hover:bg-red-700 text-white'
                                                  : 'text-red-600 border-red-300 hover:bg-red-50 hover:text-red-700'
                                              )}
                                              onClick={e => {
                                                e.stopPropagation();
                                                handleRejectRequirement(qual.id);
                                              }}
                                            >
                                              <XCircle className='h-3 w-3 mr-1' />
                                              {qual.approval_status === 'rejected' ? 'Rejected' : 'Reject'}
                                            </Button>
                                          </div>
                                        </div>

                                      </div>
                                    ))}
                                  </div>
                                </AccordionContent>
                              </AccordionItem>
                            ))}
                          </Accordion>
                        );
                      })()}
                    </div>
                  ) : (
                    /* Progress status - Site Compliance focused */
                    <div className='flex flex-col items-center py-6 px-8'>
                      <div className='w-full max-w-md'>
                        <h3 className='text-sm font-medium text-gray-700 mb-6 text-center'>
                          Site Compliance Setup
                        </h3>
                        <div className='space-y-4'>
                          {[
                            {
                              title: 'Site Information',
                              description:
                                'Building type, age, and ownership',
                              status:
                                siteType && buildingAge
                                  ? 'complete'
                                  : 'incomplete',
                              icon: Building,
                              fields: [
                                siteType && `✓ Type: ${siteType}`,
                                buildingAge && `✓ Age: ${buildingAge}`,
                                buildingOwnership && `✓ ${buildingOwnership}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Fire Safety',
                              description: 'Fire systems and equipment',
                              status: fireSystems ? 'complete' : 'incomplete',
                              icon: Shield,
                              fields: fireSystems
                                ? [
                                    `✓ ${fireSystems}`
                                  ]
                                : []
                            },
                            {
                              title: 'Electrical Systems',
                              description: 'Electrical infrastructure details',
                              status: electricalSystems ? 'complete' : 'incomplete',
                              icon: Zap,
                              fields: electricalSystems
                                ? [
                                    `✓ ${electricalSystems}`
                                  ]
                                : []
                            },
                            {
                              title: 'Water & Utilities',
                              description: 'Water and gas systems',
                              status:
                                waterSystems || gasSystems ? 'complete' : 'incomplete',
                              icon: Droplets,
                              fields: [
                                waterSystems && `✓ Water: ${waterSystems}`,
                                gasSystems && `✓ Gas systems present`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Hazards & Safety',
                              description: 'Asbestos, legionella, hazardous materials',
                              status:
                                asbestosStatus || legionellaRisk || hazardousStorage ? 'complete' : 'incomplete',
                              icon: AlertTriangle,
                              fields: [
                                asbestosStatus && `✓ Asbestos: ${asbestosStatus}`,
                                legionellaRisk && `✓ Legionella: ${legionellaRisk}`,
                                hazardousStorage && `✓ Hazardous storage: ${hazardousStorage}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Equipment (LOLER/PSSR)',
                              description: 'Lifting and pressure equipment',
                              status:
                                liftingEquipment || pressureSystems ? 'complete' : 'incomplete',
                              icon: Wrench,
                              fields: [
                                liftingEquipment && `✓ Lifting: ${liftingEquipment}`,
                                pressureSystems && `✓ Pressure: ${pressureSystems}`
                              ].filter(Boolean)
                            },
                            {
                              title: 'Site Requirements',
                              description: 'Generated compliance requirements',
                              status:
                                siteRequirements.length > 0
                                  ? 'complete'
                                  : 'incomplete',
                              icon: CheckCircle,
                              fields:
                                siteRequirements.length > 0
                                  ? [
                                      `✓ ${siteRequirements.length} requirements generated`
                                    ]
                                  : []
                            }
                          ].map((item, index, arr) => {
                            const isComplete = item.status === 'complete';
                            const Icon = item.icon;

                            return (
                              <div
                                key={index}
                                className='flex items-start gap-3'
                              >
                                <div className='relative h-full'>
                                  <div
                                    className={cn(
                                      'w-10 h-10 rounded-full flex items-center justify-center transition-colors',
                                      isComplete && 'bg-purple-600',
                                      !isComplete && 'bg-gray-100'
                                    )}
                                  >
                                    {isComplete ? (
                                      <Check className='w-5 h-5 text-white' />
                                    ) : (
                                      <Icon className='w-5 h-5 text-gray-400' />
                                    )}
                                  </div>
                                  {index < arr.length - 1 && (
                                    <div
                                      className={cn(
                                        'absolute top-10 bottom-0 left-1/2 -translate-x-1/2 w-0.5',
                                        isComplete
                                          ? 'bg-purple-200'
                                          : 'bg-gray-200'
                                      )}
                                    />
                                  )}
                                </div>
                                <div className='flex-1 pb-4'>
                                  <p
                                    className={cn(
                                      'text-sm font-medium',
                                      isComplete
                                        ? 'text-gray-900'
                                        : 'text-gray-400'
                                    )}
                                  >
                                    {item.title}
                                  </p>
                                  <p className='text-xs text-gray-500 mt-0.5'>
                                    {item.description}
                                  </p>
                                  {item.fields.length > 0 && (
                                    <div className='mt-2 space-y-1'>
                                      {item.fields.map((field, i) => (
                                        <p
                                          key={i}
                                          className='text-xs text-gray-600'
                                        >
                                          {field}
                                        </p>
                                      ))}
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>

                        {/* Progress percentage */}
                        <div className='mt-8'>
                          <div className='flex items-center justify-between text-xs text-gray-500 mb-2'>
                            <span>Overall Progress</span>
                            <span>
                              {Math.round(
                                ([
                                  siteType && buildingAge,
                                  fireSystems,
                                  electricalSystems,
                                  waterSystems || gasSystems,
                                  asbestosStatus || legionellaRisk,
                                  siteRequirements.length > 0
                                ].filter(Boolean).length /
                                  6) *
                                  100
                              )}
                              %
                            </span>
                          </div>
                          <div className='w-full bg-gray-200 rounded-full h-2'>
                            <div
                              className='bg-purple-600 h-2 rounded-full transition-all duration-300'
                              style={{
                                width: `${
                                  ([
                                    siteType && buildingAge,
                                    fireSystems,
                                    electricalSystems,
                                    waterSystems || gasSystems,
                                    asbestosStatus || legionellaRisk,
                                    siteRequirements.length > 0
                                  ].filter(Boolean).length /
                                    6) *
                                  100
                                }%`
                              }}
                            />
                          </div>
                        </div>

                        {/* Generate Requirements Button */}
                        <div className='mt-8'>
                          <Button
                            onClick={() => generateRequirementsFromWizard()}
                            disabled={isGenerating || !(siteType && buildingAge)}
                            className='w-full bg-purple-600 hover:bg-purple-700'
                          >
                            {isGenerating ? (
                              <>
                                <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                                Generating...
                              </>
                            ) : (
                              <>
                                <Wand2 className='h-4 w-4 mr-2' />
                                Generate Site Requirements
                              </>
                            )}
                          </Button>
                          <p className='text-xs text-gray-500 text-center mt-3'>
                            Or chat to collect more site details first
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Powered by Aiflows branding */}
                <div className='absolute bottom-3 left-0 right-0 flex items-center justify-center'>
                  <div className='flex items-center gap-1 text-[11px] text-gray-500 bg-white/80 backdrop-blur-sm px-3 py-1.5 rounded-full shadow-sm border border-gray-200/50'>
                    <span>Powered by</span>
                    <img
                      src='/aiflowslogo.png'
                      alt='Aiflows'
                      className='h-3.5 w-3.5 object-contain -mx-0.5'
                    />
                    <span className='font-medium'>Aiflows</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Edit Requirement Dialog */}
          <Dialog
            open={!!editingRequirement}
            onOpenChange={open => {
              if (!open) {
                setEditingRequirement(null);
                setEditFormData({});
              }
            }}
          >
            <DialogContent className='max-w-2xl max-h-[90vh] overflow-hidden p-0'>
              <div className='overflow-y-auto max-h-[90vh] p-6'>
                <DialogHeader>
                  <DialogTitle>Edit Requirement</DialogTitle>
                  <DialogDescription>
                    Modify the compliance requirement details below.
                  </DialogDescription>
                </DialogHeader>

                <div className='space-y-4 py-4'>
                  <div className='space-y-2'>
                    <Label htmlFor='edit-name'>Requirement Name</Label>
                    <Input
                      id='edit-name'
                      value={editFormData.name || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          name: e.target.value
                        }))
                      }
                      placeholder='e.g., Forklift Operator License'
                    />
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-description'>Description</Label>
                    <Textarea
                      id='edit-description'
                      value={editFormData.description || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          description: e.target.value
                        }))
                      }
                      placeholder='Detailed description of the requirement'
                      rows={3}
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='edit-category'>Category</Label>
                      <Select
                        value={editFormData.category || 'Training'}
                        onValueChange={value =>
                          setEditFormData(prev => ({
                            ...prev,
                            category: value
                          }))
                        }
                      >
                        <SelectTrigger id='edit-category'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='Equipment'>Equipment</SelectItem>
                          <SelectItem value='Safety'>Safety</SelectItem>
                          <SelectItem value='Compliance'>Compliance</SelectItem>
                          <SelectItem value='Training'>Training</SelectItem>
                          <SelectItem value='Certification'>
                            Certification
                          </SelectItem>
                          <SelectItem value='Environmental'>
                            Environmental
                          </SelectItem>
                          <SelectItem value='Quality'>Quality</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='edit-priority'>Priority</Label>
                      <Select
                        value={editFormData.priority || 'medium'}
                        onValueChange={value =>
                          setEditFormData(prev => ({
                            ...prev,
                            priority: value
                          }))
                        }
                      >
                        <SelectTrigger id='edit-priority'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='high'>High</SelectItem>
                          <SelectItem value='medium'>Medium</SelectItem>
                          <SelectItem value='low'>Low</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='edit-renewal'>Renewal Period</Label>
                      <Input
                        id='edit-renewal'
                        value={editFormData.renewal_period || ''}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            renewal_period: e.target.value
                          }))
                        }
                        placeholder='e.g., 3 years, 12 months'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='edit-status'>Status</Label>
                      <Select
                        value={editFormData.status || 'pending'}
                        onValueChange={value =>
                          setEditFormData(prev => ({ ...prev, status: value }))
                        }
                      >
                        <SelectTrigger id='edit-status'>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value='active'>Active</SelectItem>
                          <SelectItem value='pending'>Pending</SelectItem>
                          <SelectItem value='expired'>Expired</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-regulatory'>Regulatory Body</Label>
                    <Input
                      id='edit-regulatory'
                      value={editFormData.regulatory_body || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          regulatory_body: e.target.value
                        }))
                      }
                      placeholder='e.g., HSE, OSHA, ISO'
                    />
                  </div>

                  <div className='grid grid-cols-2 gap-4'>
                    <div className='space-y-2'>
                      <Label htmlFor='edit-minimum'>Minimum Personnel</Label>
                      <Input
                        id='edit-minimum'
                        value={editFormData.minimum_personnel || ''}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            minimum_personnel: e.target.value
                          }))
                        }
                        placeholder='e.g., 2, 10%, 1 per 50'
                      />
                    </div>

                    <div className='space-y-2'>
                      <Label htmlFor='edit-calculated'>Required Number</Label>
                      <Input
                        id='edit-calculated'
                        type='number'
                        value={editFormData.calculated_required || ''}
                        onChange={e =>
                          setEditFormData(prev => ({
                            ...prev,
                            calculated_required:
                              parseInt(e.target.value) || null
                          }))
                        }
                        placeholder='Actual number needed'
                      />
                    </div>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-regulation'>
                      Regulation Reference
                    </Label>
                    <Input
                      id='edit-regulation'
                      value={editFormData.regulation_reference || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          regulation_reference: e.target.value
                        }))
                      }
                      placeholder='e.g., PUWER 1998, CDM 2015'
                    />
                  </div>

                  <div className='flex items-center space-x-2'>
                    <input
                      type='checkbox'
                      id='edit-legal'
                      checked={editFormData.legal_requirement || false}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          legal_requirement: e.target.checked
                        }))
                      }
                      className='h-4 w-4 rounded border-gray-300'
                    />
                    <Label htmlFor='edit-legal' className='font-normal'>
                      This is a legal requirement
                    </Label>
                  </div>

                  <div className='space-y-2'>
                    <Label htmlFor='edit-notes'>Additional Notes</Label>
                    <Textarea
                      id='edit-notes'
                      value={editFormData.notes || ''}
                      onChange={e =>
                        setEditFormData(prev => ({
                          ...prev,
                          notes: e.target.value
                        }))
                      }
                      placeholder='Any additional information or context'
                      rows={3}
                    />
                  </div>
                </div>

                <DialogFooter>
                  <Button
                    variant='outline'
                    onClick={() => {
                      setEditingRequirement(null);
                      setEditFormData({});
                    }}
                  >
                    Cancel
                  </Button>
                  <Button onClick={handleSaveEdit} disabled={isSaving}>
                    {isSaving ? (
                      <>
                        <Loader2 className='h-4 w-4 mr-2 animate-spin' />
                        Saving...
                      </>
                    ) : (
                      <>Save Changes</>
                    )}
                  </Button>
                </DialogFooter>
              </div>
            </DialogContent>
          </Dialog>

          {/* Clear All Confirmation Dialog */}
          <AlertDialog open={showClearConfirmDialog} onOpenChange={setShowClearConfirmDialog}>
            <AlertDialogContent className="sm:max-w-md p-0 overflow-hidden">
              <div className="p-6 space-y-4">
                <div className="flex items-start gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-red-100">
                    <AlertTriangle className="h-6 w-6 text-red-600" />
                  </div>
                  <div className="flex-1 space-y-1">
                    <AlertDialogTitle className="text-lg font-semibold">Clear All Requirements</AlertDialogTitle>
                    <AlertDialogDescription>
                      This action cannot be undone.
                    </AlertDialogDescription>
                  </div>
                </div>
                <div className="rounded-lg bg-muted p-4">
                  <p className="text-sm text-muted-foreground">
                    Are you sure you want to clear all <span className="font-medium text-foreground">{siteRequirements.length}</span> site requirements from this space?
                  </p>
                </div>
                <AlertDialogFooter className="gap-3 pt-2">
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={confirmClearAllSiteRequirements}
                    className='bg-red-600 hover:bg-red-700 focus:ring-red-600'
                  >
                    Clear All
                  </AlertDialogAction>
                </AlertDialogFooter>
              </div>
            </AlertDialogContent>
          </AlertDialog>

          {/* Link Document Dialog */}
          <LinkDocumentDialog
            open={linkDialogOpen}
            onOpenChange={setLinkDialogOpen}
            projectId={projectId}
            onSelect={handleLinkDocument}
            title={linkingRequirement?.requirement_type === 'internal_checklist' ? 'Link Checklist' : 'Link Document'}
            description={`Select a file to link to "${linkingRequirement?.name || 'this requirement'}".`}
          />

          {/* Link Task/Workflow Dialog for Checklists */}
          <Dialog open={linkTaskWorkflowDialogOpen} onOpenChange={(open) => {
            setLinkTaskWorkflowDialogOpen(open);
            if (!open) setLinkingChecklistRequirement(null);
          }}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
              <DialogHeader>
                <DialogTitle>Link Task or Workflow</DialogTitle>
                <DialogDescription>
                  Select an existing task or workflow to link to "{linkingChecklistRequirement?.name || 'this checklist'}".
                </DialogDescription>
              </DialogHeader>

              <div className="flex-1 overflow-y-auto py-4">
                {loadingTasksWorkflows ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    <span className="ml-2 text-muted-foreground">Loading...</span>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {/* Tasks Section */}
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                        <CheckSquare className="h-4 w-4" />
                        Tasks ({spaceTasks.length})
                      </h3>
                      {spaceTasks.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic px-4 py-2">No tasks in this space</p>
                      ) : (
                        <div className="space-y-2">
                          {spaceTasks.map((task) => (
                            <button
                              key={task.id}
                              onClick={() => handleLinkTaskWorkflow('task', task.id)}
                              disabled={task.compliance_requirement_id && task.compliance_requirement_id !== linkingChecklistRequirement?.id}
                              className={cn(
                                "w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors",
                                task.compliance_requirement_id && task.compliance_requirement_id !== linkingChecklistRequirement?.id
                                  ? "opacity-50 cursor-not-allowed bg-muted"
                                  : "hover:bg-muted/50 hover:border-primary cursor-pointer"
                              )}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate">{task.name}</span>
                                  {task.compliance_requirement_id && (
                                    <Badge variant="secondary" className="text-xs">
                                      {task.compliance_requirement_id === linkingChecklistRequirement?.id ? 'This checklist' : 'Linked'}
                                    </Badge>
                                  )}
                                </div>
                                {task.description && (
                                  <p className="text-sm text-muted-foreground truncate mt-0.5">{task.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <Badge variant={task.status === 'completed' ? 'default' : 'outline'} className="text-xs">
                                  {task.status || 'pending'}
                                </Badge>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* Workflows Section */}
                    <div>
                      <h3 className="text-sm font-medium text-muted-foreground mb-3 flex items-center gap-2">
                        <Zap className="h-4 w-4" />
                        Workflows ({spaceWorkflows.length})
                      </h3>
                      {spaceWorkflows.length === 0 ? (
                        <p className="text-sm text-muted-foreground italic px-4 py-2">No workflows in this space</p>
                      ) : (
                        <div className="space-y-2">
                          {spaceWorkflows.map((workflow) => (
                            <button
                              key={workflow.id}
                              onClick={() => handleLinkTaskWorkflow('workflow', workflow.id)}
                              disabled={workflow.compliance_requirement_id && workflow.compliance_requirement_id !== linkingChecklistRequirement?.id}
                              className={cn(
                                "w-full flex items-center justify-between p-3 rounded-lg border text-left transition-colors",
                                workflow.compliance_requirement_id && workflow.compliance_requirement_id !== linkingChecklistRequirement?.id
                                  ? "opacity-50 cursor-not-allowed bg-muted"
                                  : "hover:bg-muted/50 hover:border-primary cursor-pointer"
                              )}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium truncate">{workflow.name}</span>
                                  {workflow.compliance_requirement_id && (
                                    <Badge variant="secondary" className="text-xs">
                                      {workflow.compliance_requirement_id === linkingChecklistRequirement?.id ? 'This checklist' : 'Linked'}
                                    </Badge>
                                  )}
                                </div>
                                {workflow.description && (
                                  <p className="text-sm text-muted-foreground truncate mt-0.5">{workflow.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-2 ml-2">
                                <Badge variant={workflow.status === 'active' ? 'default' : 'outline'} className="text-xs">
                                  {workflow.status || 'draft'}
                                </Badge>
                              </div>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <DialogFooter className="border-t pt-4">
                <Button variant="outline" onClick={() => setLinkTaskWorkflowDialogOpen(false)}>
                  Cancel
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </>
      </AppShell3>
    </PageTransition>
  );
};

export default SiteCompliancePage;
